import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useNavigate } from "@tanstack/react-router";
import { useTables } from "@/hooks/use-tables";
import { useActiveLocationTimezone, useLocationSettings } from "@/hooks/use-locations";
import { useOpeningHours } from "@/hooks/use-opening-hours";
import { useAvailability } from "@/hooks/use-availability";
import { useInvitees } from "@/hooks/use-invitees";
import { useCalendarDisabled } from "@/hooks/use-calendar-disabled";
import {
  useActiveReservations,
  useReservationEvents,
  useReservationSearch,
} from "@/hooks/use-reservations";
import { useWaitlist } from "@/hooks/use-waitlist";
import { useCotizaciones } from "@/hooks/use-cotizaciones";
import { CardConfirmRow } from "@/components/CardConfirmRow";
import { WaitlistCard } from "@/components/WaitlistCard";
import { WaitlistDetailView } from "@/components/WaitlistDetailView";
import NewWaitlistView from "@/components/WalkInWaitlistModal";
import { AccIcon, WhatsAppIcon } from "@/components/icons";
import { SectionLabel } from "@/components/modal-fields";
import { NO_SHOW_DETAIL_CLASS, NoShowButton } from "@/components/NoShowButton";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { performMidServiceMove } from "@/components/ReservationContextMenu";
import { TableExplorer, type TableExplorerPick } from "@/components/TableExplorer";
import {
  type ExplorerResult,
  apiApplyPlan,
  apiDryRun,
  apiExplore,
  apiSeatReservation,
  apiSetPinLevel,
  apiUpdateReservation,
  readErrorDetail,
} from "@/lib/reservation-api";
import { useZones } from "@/hooks/use-zones";
import { useGuestDuplicates, useGuestStats, useGuests } from "@/hooks/use-guests";
import { isValidPhone, isoForDialCode, normalizePhoneForCompare, splitDialCode } from "@/lib/phone";

/** Heuristic: did the operator type a phone number into the "Nombre o
 *  teléfono…" search? Counts digits and spacers. Anything with ≥6 digits
 *  and ≥70 % numeric characters reads as a phone — anything else stays a
 *  name. Used by the "Crear cliente nuevo" paths so a typed phone lands
 *  in ``guestPhone`` instead of ``guestName`` (which used to silently
 *  re-link to the existing CRM record at save time). */
function looksLikePhone(input: string): boolean {
  const trimmed = input.trim();
  if (trimmed.length < 6) return false;
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 6) return false;
  const nonAlpha = trimmed.replace(/[a-záéíóúñü]/gi, "");
  return nonAlpha.length / trimmed.length >= 0.7;
}
import { ReservationTagPicker } from "@/components/ReservationTagPicker";
import { reservationTagLabel } from "@/lib/reservation-tags";
import { tableFeatureLabel } from "@/lib/table-features";
import type {
  ApiReservationFeature,
  ApiReservationTag,
  ApiTableFeature,
  ApiWaitlistEntry,
} from "@/types/api";
import {
  Accessibility,
  CalendarCheck,
  CalendarIcon,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Dog,
  Link2,
  Lock,
  MapPin,
  MessageSquare,
  Moon,
  MoreHorizontal,
  PenLine,
  Plus,
  QrCode,
  Search,
  Star,
  Sun,
  Undo2,
  Unlock,
  X,
} from "lucide-react";

import {
  cancelReservation,
  createReservation,
  isReservationSoon,
  markCompleted,
  markNoShow,
  markSeated,
  notify,
  requestMapFit,
  revertNoShow,
  setFocusTableIds,
  setPreviewTime,
  toggleConfirmedByHost,
  unseatReservation,
  useAppStore,
  useLiveReservations,
  useTodayReservations,
  useTodayReservationsAll,
} from "@/store/store";
import { useShallow } from "zustand/shallow";
import type { Guest, Reservation, ReservationPinLevel, StatusTab, Table } from "@/store/types";
import { type WalkinOnMyMesa, computeWalkinOnMyMesaMap } from "@/lib/walkin-overlap";
import { zoneAccent } from "@/lib/zone-brand-colors";
import { formatCancelledPill } from "@/lib/cancelled-format";
import { computeExistingDurationMin } from "@/lib/reservation-duration";
import {
  dayjs,
  formatBookingDateLabel,
  formatTime,
  minutesRemaining,
  minutesUntilNoShow,
  parseInTz,
  parseTime,
  parseTimeHour,
  todayStr,
} from "@/utils/date";
import { confirmReservationTables } from "@/store/walk-in-zone-confirm-store";

// Mirrors the same threshold inside ``useReservationSearch``: at least this
// many characters before we hit the backend or branch the sidebar into search
// mode. With one char we'd match nearly the whole table.
const SEARCH_MIN_LEN = 2;

const occasionLabels: Record<string, string> = {
  birthday: "Cumpleaños",
  anniversary: "Aniversario",
  date: "Cita",
  business: "Reunión",
  other: "Otro",
};

const originLabels: Record<string, string> = {
  google: "Google",
  phone: "Llamada",
  seimseim: "Seim·Seim",
  walkin: "Walk-in",
  web: "Web",
  whatsapp: "WhatsApp",
  agent: "Agente",
};

/** Force a re-render every 60 s so relative-time badges stay fresh. */
function useMinuteTick() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
}

// Toggleable affordance for boolean reservation features (perrito,
// silla) en el bloque Cliente del DetailView. Cuando está seteada
// se ve igual que el chip read-only (sólido, borde gris); cuando no,
// queda como ``+ icono`` con borde dashed para que el host vea que es
// agregable. Diseño matchea el mockup que mandó la PM (perrito sólido
// + silla pendiente).
function FeatureToggleChip({
  selected,
  onClick,
  label,
  icon,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={selected ? `Quitar ${label.toLowerCase()}` : `Agregar ${label.toLowerCase()}`}
      aria-pressed={selected}
      className={
        "inline-flex h-5 items-center justify-center rounded text-text-secondary transition-colors " +
        (selected
          ? "w-5 border border-[#c8c8c2] bg-[#f7f6f3] hover:bg-[#ececea]"
          : "gap-0.5 border border-dashed border-text-secondary/60 bg-white px-1 hover:bg-[#f7f6f3]")
      }
    >
      {!selected && <Plus className="size-2.5" aria-hidden="true" />}
      {icon}
    </button>
  );
}

// Natural compare for table names like ``M20``, ``B5``, ``T2``,
// ``P10``. Sort by prefix letters first (alphabetical), then by the
// numeric suffix as a number (so M20 sorts before M100 instead of
// after, the way the raw localeCompare would). Used to render
// selected-mesa chips in ascending order regardless of pick order.
function compareTableNames(a: string, b: string): number {
  const matchA = a.match(/^([A-Za-z]*)(\d+)/);
  const matchB = b.match(/^([A-Za-z]*)(\d+)/);
  if (!matchA || !matchB) return a.localeCompare(b);
  const [, prefixA, numA] = matchA;
  const [, prefixB, numB] = matchB;
  if (prefixA !== prefixB) return prefixA.localeCompare(prefixB);
  return parseInt(numA, 10) - parseInt(numB, 10);
}

// Window after seating during which the "Revertir Sentar" undo stays available.
const UNSEAT_WINDOW_MINUTES = 5;

// Reacomodo automático — el ``pin_level`` que el motor respeta al
// reubicar reservas. Vive dentro de la sección Mover mesa del detalle
// porque controla *cómo* el motor mueve la reserva (es un sub-control
// de Mover, no un menú aparte).
const PIN_OPTIONS: Array<{ value: ReservationPinLevel; label: string; icon: ReactNode }> = [
  { value: "none", label: "Sí", icon: <Unlock className="size-3" /> },
  { value: "zone", label: "En su zona", icon: <MapPin className="size-3" /> },
  { value: "table", label: "Fija", icon: <Lock className="size-3" /> },
];

// Plain-language explanation of the active reubicación level — the line
// the user loved in the handoff ("En su zona: el sistema puede moverla a
// otra mesa, pero solo dentro de Terraza."). The lead word is bold.
function pinHelper(pin: ReservationPinLevel, zoneName: string): { lead: string; rest: string } {
  if (pin === "table") {
    return { lead: "Fija:", rest: " nadie en el sistema la mueve — se queda en esta mesa." };
  }
  if (pin === "none") {
    return {
      lead: "Sí:",
      rest: " el sistema puede moverla a cualquier mesa libre, en cualquier zona.",
    };
  }
  return {
    lead: "En su zona:",
    rest: ` el sistema puede moverla a otra mesa, pero solo dentro de ${zoneName || "su zona"}.`,
  };
}

// Spanish labels for the field names the backend emits in the detail
// of an "edited" event (e.g. "internal_notes, pax"). Keeps the timeline
// readable to a hostess who doesn't think in API field names.
const EDITED_FIELD_LABEL: Record<string, string> = {
  internal_notes: "notas internas",
  notes: "notas del cliente",
  pax: "personas",
  starts_at: "hora",
  ends_at: "duración",
  tables: "mesa",
  preferred_zone_id: "zona",
  tags: "restricciones",
  features: "preferencias",
  occasion: "ocasión",
  is_vip: "VIP",
  delay_minutes: "retraso",
  guest_name: "nombre del cliente",
  guest_phone: "teléfono",
  guest_email: "email",
};

function formatHistoryWhen(ts: number): string {
  const now = dayjs();
  const d = dayjs(ts);
  const time = d.format("h:mm A").toLowerCase();
  if (d.isSame(now, "day")) return `hoy · ${time}`;
  if (d.isSame(now.subtract(1, "day"), "day")) return `ayer · ${time}`;
  return `${d.format("D MMM")} · ${time}`.toLowerCase();
}

export function formatHistoryWhat(
  event: { action: string; detail?: string; causedByLabel?: string },
  origin: string,
): string {
  const detail = (event.detail ?? "").trim();
  switch (event.action) {
    case "rearranged": {
      // Una reserva desplazada no pidió moverse. Decir para quién se liberó
      // la mesa es lo único que responde "¿y por qué me movieron?".
      const cause = (event.causedByLabel ?? "").trim();
      // La causa ya trae su propia hora entre paréntesis; encadenarla con "·"
      // dejaba dos relojes y cuatro separadores en un renglón que el
      // anfitrión lee de reojo en pleno servicio.
      const moved = detail.replace(/^Mesas? reasignadas?:\s*/i, "");
      if (cause) return `Movida por la reserva de ${cause}${moved ? ` · ${moved}` : ""}`;
      return moved ? `Mesa reubicada: ${moved}` : "Mesa reubicada";
    }
    case "created": {
      const originLabel = originLabels[origin] ?? origin;
      return `Reserva creada · ${originLabel}`;
    }
    case "confirmed":
      return detail.toLowerCase() === "unconfirmed" ? "Confirmación quitada" : "Reserva confirmada";
    case "edited": {
      const fieldsEs = detail
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean)
        .map((f) => EDITED_FIELD_LABEL[f] ?? f)
        .join(", ");
      return fieldsEs ? `Reserva editada: ${fieldsEs}` : "Reserva editada";
    }
    case "tables_assigned":
      return detail ? `Mesa asignada: ${detail}` : "Mesa asignada";
    case "seated": {
      const m = /(\d{1,2}:\d{2})/.exec(detail);
      return m ? `Reserva sentada a las ${m[1]}` : "Reserva sentada";
    }
    case "completed":
      return "Visita finalizada";
    case "no_show_marked":
      return "Marcada como no-show";
    case "cancelled":
      return detail ? `Reserva cancelada · ${detail}` : "Reserva cancelada";
    case "reverted": {
      const d = detail.toLowerCase();
      if (d.includes("seating") || d.includes("sentada")) return "Se revirtió marcar como sentada";
      if (d.includes("no-show") || d.includes("no_show") || d.includes("noshow"))
        return "Se revirtió el no-show";
      return detail ? `Reserva revertida · ${detail}` : "Reserva revertida";
    }
    case "invitee_added":
      return detail ? `Invitado agregado: ${detail}` : "Invitado agregado";
    case "invitee_removed":
      return detail ? `Invitado quitado: ${detail}` : "Invitado quitado";
    case "delayed": {
      if (detail.includes("removido")) return "Retraso removido";
      const m = /\+?(\d+)\s*min/.exec(detail);
      return m ? `Reserva retrasada por ${m[1]} min` : "Reserva retrasada";
    }
    default:
      return detail ? `${event.action} · ${detail}` : event.action;
  }
}

/** Minutes elapsed since a reservation was seated. ``seatedAt`` is an ISO
 *  datetime from the API or an "h:mm A" string from an optimistic local
 *  seat — handle both. Returns null when it can't be determined. */
function minutesSinceSeated(seatedAt: string, date: string): number | null {
  if (!seatedAt) return null;
  const seated = /\d{4}-\d{2}-\d{2}/.test(seatedAt)
    ? dayjs(seatedAt)
    : dayjs(`${date} ${seatedAt}`, "YYYY-MM-DD h:mm A");
  if (!seated.isValid()) return null;
  return dayjs().diff(seated, "minute");
}

// ── Compact card for the list ───────────────────────────────────
function ReservationCard({
  r,
  onSelect,
  walkinOnMyMesa = null,
}: {
  r: Reservation;
  onSelect: (reservation: Reservation) => void;
  /** Walk-in occupancy data, precomputed by the list parent so each
   *  card avoids subscribing to ``useLiveReservations`` on its own. */
  walkinOnMyMesa?: WalkinOnMyMesa | null;
}) {
  useMinuteTick();
  const {
    highlightedReservationId,
    setHighlightedTableIds,
    tables,
    zones,
    noShowToleranceMinutes,
  } = useAppStore(
    useShallow((s) => ({
      highlightedReservationId: s.highlightedReservationId,
      setHighlightedTableIds: s.setHighlightedTableIds,
      tables: s.tables,
      zones: s.zones,
      noShowToleranceMinutes: s.restaurantConfig.noShowToleranceMinutes,
    })),
  );
  const [confirm, setConfirm] = useState<"cancel" | "noshow" | null>(null);

  const status = r.status;
  const isBooked = status === "reservada";

  // No-show window: booked, start time passed, still inside the tolerance.
  const soon = isBooked && isReservationSoon(r);
  const noShowMin =
    isBooked && soon ? minutesUntilNoShow(r.time, noShowToleranceMinutes, r.date) : null;
  const inNoShowWindow =
    noShowMin !== null && noShowMin >= 0 && minutesRemaining(r.time, r.date) === 0;
  const noShowPct =
    inNoShowWindow && noShowToleranceMinutes > 0
      ? Math.max(0, Math.min(100, (noShowMin / noShowToleranceMinutes) * 100))
      : 0;

  // Accessory flags.
  const hasPet = r.features.includes("pet_friendly");
  const hasAccessible = r.features.includes("accessible");
  const hasNote =
    Boolean(r.notes?.trim()) || (!!r.occasion && r.occasion !== "none") || r.tags.length > 0;
  const isRisk = r.guestIsRisk ?? false;

  const tone =
    status === "noshow" || status === "cancelada"
      ? "border-[#b13030] bg-white"
      : status === "completada"
        ? "border-green-300 bg-white"
        : inNoShowWindow
          ? "border-[#b88a00] bg-white"
          : isBooked && r.isConfirmedByHost
            ? "border-green-300 bg-white"
            : soon
              ? "border-[#2a4a7a] bg-[#eef4ff]"
              : "border-[#c8c8c2] bg-white";

  const isHighlighted = highlightedReservationId === r.id;
  // Each mesa chip is tinted with its zone's accent — the same fill the
  // map and the Timeline paint for that zone (``zoneAccent`` is the
  // shared resolver). Lets service staff tie a card to a spot on the
  // floor at a glance. Falls back to neutral grey when the table or its
  // zone can't be resolved.
  const tableChips = r.tableIds
    .map((id) => {
      const t = tables.find((tb) => tb.id === id);
      if (!t) return null;
      const zone = zones.find((z) => z.id === t.zoneId);
      return { name: t.name, bg: zone ? zoneAccent(zone).fill : "#ededea" };
    })
    .filter((c): c is { name: string; bg: string } => c !== null)
    // Natural ascending so the strip reads 20·30·40 (not 30·40·20) and
    // T2 doesn't get pushed past T10.
    .toSorted((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
    );

  // Finalizadas pill — reuses the walk-in pill shape. Cancelled rows
  // carry the enriched "Cancelada hace 2h / hoy 2:30 PM / ayer …" label
  // so the operator can scan the cancellation freshness without
  // opening the detail.
  const finalizedPill =
    status === "noshow"
      ? "No Show"
      : status === "cancelada"
        ? formatCancelledPill(r.cancelledAt)
        : null;

  // "Revertir Sentar" stays available only briefly after seating.
  const minsSinceSeated = status === "sentado" ? minutesSinceSeated(r.seatedAt, r.date) : null;
  const canUnseat = minsSinceSeated !== null && minsSinceSeated < UNSEAT_WINDOW_MINUTES;

  // Walk-in pill (booked cards only): which of my tables a walk-in holds.
  const walkinPill =
    isBooked && walkinOnMyMesa
      ? (() => {
          const t = r.tableIds
            .map((id) => tables.find((tt) => tt.id === id))
            .find((tt) => !!tt && walkinOnMyMesa.other.tableIds.includes(tt.id));
          return t ? { table: t.name, freeAt: walkinOnMyMesa.other.endTime } : null;
        })()
      : null;

  const openWhatsApp = (e: ReactMouseEvent) => {
    e.stopPropagation();
    const digits = r.guestPhone.replace(/\D/g, "");
    if (digits) window.open(`https://wa.me/${digits}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-reservation-id={r.id}
      onClick={() => {
        onSelect(r);
        setHighlightedTableIds(r.tableIds);
      }}
      className={`cursor-pointer rounded-[10px] border p-3 transition ${tone} ${
        isHighlighted ? "ring-2 ring-[#2a4a7a] ring-offset-1" : ""
      }`}
    >
      {walkinPill && (
        <div className="mb-1.5 inline-flex w-fit items-center gap-1.5 rounded-full border border-[#8a7547] bg-[#f0eadf] py-0.5 pl-1.5 pr-2.5 font-mono text-[10px] font-bold uppercase tracking-wide text-[#6b5933]">
          <span className="inline-flex items-center gap-1">
            <span aria-hidden="true">⊕</span>
            <span className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded border border-[#8a7547] bg-white px-1 text-[11px] font-extrabold leading-none">
              {walkinPill.table}
            </span>
          </span>
          <span>con walk-in se libera {walkinPill.freeAt}</span>
        </div>
      )}
      {finalizedPill && (
        <div className="mb-1.5 inline-flex w-fit items-center rounded-full border border-[#b13030] bg-white px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-[#b13030]">
          {finalizedPill}
        </div>
      )}

      <div className="flex items-baseline gap-2">
        <div className="min-w-0 flex-1 text-[19px] font-bold leading-tight tracking-tight">
          <span className="align-middle">{r.guestName}</span>
          {(r.isVip || hasPet || hasAccessible || hasNote || isRisk) && (
            <span className="ml-1.5 inline-flex gap-1 align-middle">
              {r.isVip && (
                <AccIcon>
                  <Star className="size-2.5 text-orange-500" fill="currentColor" />
                </AccIcon>
              )}
              {hasPet && (
                <AccIcon>
                  <Dog className="size-3" />
                </AccIcon>
              )}
              {hasAccessible && (
                <AccIcon>
                  <Accessibility className="size-3" />
                </AccIcon>
              )}
              {hasNote && (
                <AccIcon>
                  <MessageSquare className="size-3" />
                </AccIcon>
              )}
              {isRisk && (
                <span className="inline-flex h-[18px] items-center rounded bg-[#b13030] px-1.5 text-[9px] font-extrabold uppercase tracking-wide text-white">
                  Riesgo
                </span>
              )}
            </span>
          )}
        </div>
        <div className="shrink-0 font-mono text-[13px] font-bold">{r.time}</div>
        <ChevronRight className="size-4 shrink-0 self-center text-[#888]" />
      </div>

      {/* Mesas + Pax — la mesa es el dato dominante para el operador
          (a qué mesa va el guest), así que ocupa el lado izquierdo y
          crece para que los chips wrappeen cuando hay grupos de 8-12
          mesas. El pax queda como dato secundario a la derecha. */}
      <div className="mt-2 grid grid-cols-[1fr_auto] items-stretch overflow-hidden rounded-lg border border-[#111] bg-white/70">
        <div className="flex flex-col gap-1 border-r border-[#c8c8c2] px-3 py-1.5">
          <span className="font-mono text-[9px] font-medium uppercase tracking-widest text-[#888]">
            {tableChips.length > 1 ? "Mesas" : "Mesa"}
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {tableChips.length > 0 ? (
              tableChips.map((c, i) => (
                <span
                  key={`${c.name}-${i}`}
                  style={{ backgroundColor: c.bg }}
                  className="rounded px-2.5 py-1 font-mono text-[15px] font-bold leading-none text-[#1a1a1a]"
                >
                  {c.name}
                </span>
              ))
            ) : (
              <span className="text-[13px] text-[#888]">Sin mesa</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 px-3.5 py-1.5 text-center">
          <span className="font-mono text-[9px] font-medium uppercase tracking-widest text-[#888]">
            Pax
          </span>
          <div className="flex flex-1 items-center justify-center">
            <span className="font-mono text-[20px] font-extrabold leading-none">{r.pax}</span>
          </div>
        </div>
      </div>

      {inNoShowWindow && (
        <>
          <div className="mt-2 h-[3px] overflow-hidden rounded-sm bg-[#fff2c4]">
            <div className="h-full bg-[#b88a00]" style={{ width: `${noShowPct}%` }} />
          </div>
          <div className="mt-1 flex justify-between font-mono text-[10px] font-bold uppercase tracking-wide text-[#b88a00]">
            <span>Ventana de no-show</span>
            <span>{noShowMin}m restantes</span>
          </div>
        </>
      )}

      {isBooked && confirm === null && (
        <div className="mt-2 flex items-stretch gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              markSeated(r.id);
            }}
            className="flex min-h-9 flex-1 items-center justify-center rounded-lg bg-[#111] px-2.5 text-xs font-semibold text-white"
          >
            Sentar
          </button>
          {inNoShowWindow ? (
            <NoShowButton
              className="gap-1"
              onClick={(e) => {
                e.stopPropagation();
                setConfirm("noshow");
              }}
            >
              No-show
              <span className="font-mono text-[11px] font-bold opacity-80">{noShowMin}m</span>
            </NoShowButton>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setConfirm("cancel");
              }}
              className="flex min-h-9 items-center justify-center rounded-lg border border-[#c8c8c2] px-3.5 text-xs font-medium text-[#888]"
            >
              Cancelar
            </button>
          )}
          <button
            type="button"
            onClick={openWhatsApp}
            disabled={!r.guestPhone}
            aria-label="Contactar por WhatsApp"
            className="flex min-h-9 w-11 shrink-0 items-center justify-center rounded-lg border border-[#c8c8c2] bg-white disabled:opacity-40"
          >
            <WhatsAppIcon />
          </button>
        </div>
      )}
      {isBooked && confirm === "cancel" && (
        <CardConfirmRow
          tone="red"
          label="¿Cancelar reserva?"
          yesLabel="Sí, cancelar"
          onYes={(e) => {
            e.stopPropagation();
            cancelReservation(r.id, "");
            setConfirm(null);
          }}
          onNo={(e) => {
            e.stopPropagation();
            setConfirm(null);
          }}
        />
      )}
      {isBooked && confirm === "noshow" && (
        <CardConfirmRow
          tone="warn"
          label="¿Confirmar No-Show?"
          yesLabel="Sí, no-show"
          onYes={(e) => {
            e.stopPropagation();
            markNoShow(r.id);
            setConfirm(null);
          }}
          onNo={(e) => {
            e.stopPropagation();
            setConfirm(null);
          }}
        />
      )}
      {status === "sentado" && canUnseat && (
        <div className="mt-2 flex">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              unseatReservation(r.id);
            }}
            className="flex min-h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border-2 border-[#111] bg-white text-xs font-semibold text-[#111]"
          >
            <Undo2 className="size-3.5" />
            Revertir Sentar
          </button>
        </div>
      )}
      {status === "noshow" && (
        <div className="mt-2 flex">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              revertNoShow(r.id);
            }}
            className="flex min-h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#b88a00] bg-[#fff2c4] text-xs font-semibold text-[#8a6d00]"
          >
            <Undo2 className="size-3.5" />
            Revertir No-show / Sentar
          </button>
        </div>
      )}
    </div>
  );
}

// ── Full detail panel ───────────────────────────────────────────
function DetailView({ detailId, onClose }: { detailId: string; onClose: () => void }) {
  useMinuteTick();
  const todayReservations = useTodayReservationsAll();
  const { zones, tables, setHighlightedReservationId, setHighlightedTableIds } = useAppStore(
    useShallow((s) => ({
      setHighlightedReservationId: s.setHighlightedReservationId,
      setHighlightedTableIds: s.setHighlightedTableIds,
      tables: s.tables,
      zones: s.zones,
    })),
  );
  const [isEditing, setIsEditing] = useState(false);
  // Mirror local edit state into the global store so other surfaces
  // (DashboardLayout's "Nueva reserva" CTA, mobile FAB) can hide
  // their entry points while an edit is in flight. Clean up on
  // unmount so a sidebar dismounting mid-edit doesn't leak a stale
  // ``true``.
  useEffect(() => {
    const { setIsEditingReservation } = useAppStore.getState();
    setIsEditingReservation(isEditing);
    return () => {
      useAppStore.getState().setIsEditingReservation(false);
    };
  }, [isEditing]);
  const [editPax, setEditPax] = useState(0);
  const [editDate, setEditDate] = useState("");
  const [editZone, setEditZone] = useState("");
  const [editTableIds, setEditTableIds] = useState<string[]>([]);
  const [editTime, setEditTime] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editFeatures, setEditFeatures] = useState<ApiReservationFeature[]>([]);
  const [editTags, setEditTags] = useState<ApiReservationTag[]>([]);
  const [editIsVip, setEditIsVip] = useState(false);
  const [explorerMode, setExplorerMode] = useState<
    null | "reassign" | "seat-override" | "mid-service"
  >(null);
  // Mesas que el operador acaba de pickear pero cuyo refetch del store
  // todavía no aterrizó. Mantener este estado nos permite reemplazar el
  // valor de "Mesa" con las mesas pendientes + un spinner inline, en
  // lugar de seguir mostrando las viejas hasta que React Query refresque.
  const [pendingTableIds, setPendingTableIds] = useState<string[] | null>(null);
  // New-design state: the panel auto-saves field edits, so the only
  // staged interactions are the two-step confirms below.
  const [cerrarConfirm, setCerrarConfirm] = useState(false);
  const [moverSeatedConfirm, setMoverSeatedConfirm] = useState(false);
  const [historyTab, setHistoryTab] = useState<"reserva" | "cliente">("reserva");
  const [showAllReservaHistory, setShowAllReservaHistory] = useState(false);
  // Brief visual confirmation that a copy-to-clipboard actually landed
  // (the icon swaps to a check for ~1.5s). Keyed per field so phone /
  // email / link don't blink at the same time when one is copied.
  const { copiedKey, copy: copyToClipboard, reset: resetCopiedKey } = useCopyToClipboard();
  // Header "Editar" abre modo edición; el ⋯ (Confirmar / Compartir /
  // Cancelar) abre una barra de confirm inline debajo del header.
  // Mutuamente exclusiva — una barra de confirm a la vez.
  const [headerConfirm, setHeaderConfirm] = useState<null | "confirm" | "cancel">(null);
  // Kebab "⋯" menu in the header — holds Confirmar / Compartir / Cancelar
  // (the actions that aren't a mode like Editar). Controlled so menu
  // items can close it on click.
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  // ``pin_level`` que el motor respeta al reubicar — sub-control del
  // bloque "Mesa · Zona".
  const [pinBusy, setPinBusy] = useState<ReservationPinLevel | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<string>("");
  // Notas internas casi siempre van vacías, así que se muestran
  // colapsadas ("+ Agregar") hasta que el host las expande o ya hay
  // contenido guardado. Ver bloque NOTAS INTERNAS abajo.
  const [notesExpanded, setNotesExpanded] = useState(false);
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { data: zonesData } = useZones();
  const { data: reservationEvents } = useReservationEvents(detailId);
  const locationTz = useActiveLocationTimezone();

  // ``useCalendarDisabled`` ya consume opening_hours + closures +
  // timezone del local — fuente de verdad compartida con
  // NewReservationModal para que ambos pickers bloqueen los mismos
  // días. Availability alimenta el select de hora con las franjas
  // reales que devuelve el backend para esa fecha/pax.
  const isDateDisabled = useCalendarDisabled();

  const { data: invitees } = useInvitees(detailId);
  const r = todayReservations.find((res) => res.id === detailId);

  // Guest visit tallies for the "X visitas · Y no-show" chips. Called
  // unconditionally (before the ``if (!r) return null`` guard) so the
  // hook order stays stable; the query itself is disabled until a
  // ``guestId`` exists. Walk-ins / anonymous rows simply skip it.
  const { data: guestStats } = useGuestStats(r?.guestId ?? undefined);

  // Keep the inline notas-internas editor in sync with the source of
  // truth — switching to a different reservation or absorbing a backend
  // refetch should reset the draft, but local typing must not be
  // clobbered mid-edit (we only sync when the *reservation id* changes
  // or the server's value differs from what we're showing for a
  // settled draft).
  useEffect(() => {
    if (!r) return;
    setNotesDraft(r.internalNotes ?? "");
    setNotesExpanded(false);
    setCerrarConfirm(false);
    setMoverSeatedConfirm(false);
    setShowAllReservaHistory(false);
    resetCopiedKey();
    setHeaderConfirm(null);
    setActionsMenuOpen(false);
    setPinError(null);
    // Si el panel cambia a otra reserva (notificación, link, etc)
    // mientras esta estaba en modo edit, descartamos el edit en
    // curso silenciosamente. Sin esto los drafts (editPax,
    // editName, …) quedaban de la reserva anterior, mostrando un
    // estado fantasma sobre los datos de la nueva.
    setIsEditing(false);
    setEditPendingMoves([]);
    setEditExplorerOpen(false);
  }, [r?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cuando el refetch trae las mesas nuevas, limpiar el pending para
  // dejar de mostrar el spinner. Comparamos como sets para tolerar
  // diferencias de orden.
  const currentTableKey = r ? [...r.tableIds].toSorted().join(",") : "";
  useEffect(() => {
    if (!pendingTableIds) return;
    if (!currentTableKey) return;
    const pendingKey = [...pendingTableIds].toSorted().join(",");
    if (currentTableKey === pendingKey) setPendingTableIds(null);
  }, [currentTableKey, pendingTableIds]);

  // Every hook below this point must run on every render. The
  // ``if (!r) return null`` guard further down would otherwise make
  // React see fewer hooks on renders where the reservation falls out
  // of the day filter (e.g. after saving a new date that moves the
  // booking to a different ``selectedDate``).
  const pendingEditId = useAppStore((s) => s.pendingEditReservationId);
  const setPendingEditId = useAppStore((s) => s.setPendingEditReservationId);
  // Auto-enter edit mode when another surface (the map's "Reasignar
  // mesa" chip, for instance) requests it via the pending flag. We
  // snapshot and clear the flag so reopening the same reservation from
  // the sidebar list doesn't keep relaunching edit mode.
  useEffect(() => {
    if (!r) return;
    if (pendingEditId && pendingEditId === r.id && !isEditing) {
      setEditPax(r.pax);
      setEditDate(r.date);
      setEditZone(r.preferredZone || "");
      setEditTableIds([...r.tableIds]);
      setEditTime(r.time);
      setEditNotes(r.notes || "");
      setEditName(r.guestName);
      setEditPhone(r.guestPhone);
      setEditEmail(r.guestEmail);
      setEditFeatures([...(r.features ?? [])] as ApiReservationFeature[]);
      setEditTags([...(r.tags ?? [])] as ApiReservationTag[]);
      setEditIsVip(r.isVip ?? false);
      setHeaderConfirm(null);
      setIsEditing(true);
      setPendingEditId(null);
    }
  }, [pendingEditId, r?.id, isEditing, setPendingEditId]);

  // Existing reservation duration in minutes — preserved across time/
  // date edits so the operator only has to pick the new start.
  const existingDurationMin = useMemo(() => {
    if (!r) return 0;
    return computeExistingDurationMin(r, locationTz);
  }, [r, locationTz]);
  const editStartsAt = useMemo(
    () =>
      editDate && editTime
        ? parseInTz(`${editDate} ${editTime}`, "YYYY-MM-DD h:mm A", locationTz).toISOString()
        : "",
    [editDate, editTime, locationTz],
  );
  const editEndsAt = useMemo(
    () =>
      editStartsAt ? dayjs(editStartsAt).add(existingDurationMin, "minute").toISOString() : "",
    [editStartsAt, existingDurationMin],
  );

  // Live validation: every time the operator tweaks pax / date / time /
  // zone, ask the backend whether the current ``editTableIds`` still
  // accommodate the booking. Used to surface the ✅/⚠️ banner and to
  // gate the Save button.
  const dryRunQuery = useQuery({
    queryKey: [
      "edit-dry-run",
      r?.id,
      editPax,
      editStartsAt,
      editEndsAt,
      editZone,
      editTableIds.join(","),
      [...editFeatures].toSorted().join(","),
    ],
    queryFn: () =>
      apiDryRun({
        pax: editPax,
        starts_at: editStartsAt,
        ends_at: editEndsAt,
        preferred_zone_id: editZone || null,
        table_ids: editTableIds,
        features: editFeatures,
        // ``enabled`` guarantees ``r`` exists by the time queryFn runs.
        exclude_reservation_id: r?.id ?? "",
      }),
    enabled:
      Boolean(r) && isEditing && editPax > 0 && Boolean(editStartsAt) && editTableIds.length > 0,
    staleTime: 5_000,
  });

  // Surface a "estás ocupando más mesas de las necesarias" hint when
  // a strict subset of the currently-picked mesas would already fit
  // ``editPax``. Mirrors the backend's no-waste subset detector;
  // computed locally so the operator gets the nudge — and a one-click
  // apply — BEFORE saving. Bitmask iteration is trivial; picks rarely
  // exceed 3 mesas.
  const wasteSuggestions: Array<{ ids: string[]; label: string }> = useMemo(() => {
    if (!isEditing || editTableIds.length < 2 || editPax <= 0) return [];
    const picked = editTableIds
      .map((id) => tables.find((t) => t.id === id))
      .filter(Boolean) as Table[];
    if (picked.length < 2) return [];
    const n = picked.length;
    const fullMask = (1 << n) - 1;
    // Collect every strict subset whose capacity range covers pax.
    // Prefer the smallest viable subsets so the operator's options are
    // ordered "fewest mesas first".
    const candidates: Array<{ ids: string[]; label: string; size: number }> = [];
    for (let mask = 1; mask < fullMask; mask++) {
      const subset = picked.filter((_, i) => mask & (1 << i));
      const subMax = subset.reduce((acc, t) => acc + (t.maxCapacity ?? 0), 0);
      // Only the cap ceiling matters here: if pax already fits in a
      // strict subset's union max, holding the surplus mesas is waste
      // regardless of per-table min_capacity floors. pax=1 on a min-2
      // mesa is still less wasteful than pax=1 holding 2 min-2 mesas.
      if (editPax <= subMax) {
        candidates.push({
          ids: subset.map((t) => t.id),
          label: subset.map((t) => t.name).join(" + "),
          size: subset.length,
        });
      }
    }
    if (!candidates.length) return [];
    const minSize = Math.min(...candidates.map((c) => c.size));
    return candidates.filter((c) => c.size === minSize).map(({ ids, label }) => ({ ids, label }));
  }, [isEditing, editTableIds, editPax, tables]);
  const wasteHint = wasteSuggestions.length > 0;

  // Franjas válidas para editar — alimenta el select de hora con los
  // mismos slots que el backend ofrece a NewReservationModal. En
  // SEATED no permitimos cambiar fecha/hora, así que no pedimos.
  const { data: editAvailability, isLoading: loadingEditAvailability } = useAvailability({
    targetDate: editDate,
    pax: editPax,
    enabled: isEditing && r?.status === "reservada",
  });

  // Detecta cuando los slots cacheados son de una fecha distinta a la
  // que la operadora tiene seleccionada — pasa entre que cambia
  // ``editDate`` y llega la respuesta nueva. ``placeholderData`` del
  // hook mantiene los slots viejos visibles para no parpadear en
  // cambios de pax, pero si el día cambió esos slots no aplican: el
  // operador podría pickear una hora que el backend luego rechaza.
  const editAvailabilityIsStale = Boolean(editAvailability && editAvailability.date !== editDate);

  const editAvailabilityTimes = useMemo(() => {
    const slots = editAvailability?.slots ?? [];
    const opts = slots.map((s) => ({
      time: formatTime(locationTz ? dayjs(s.starts_at).tz(locationTz) : dayjs(s.starts_at)),
      afterHours: Boolean(s.after_hours),
    }));
    // La hora actual de la reserva siempre debe ser seleccionable —
    // aunque el backend ya no la liste por capacidad o porque la franja
    // se llenó, el operador ya la tiene ocupada y no queremos forzarle a
    // moverla por accidente al abrir Editar.
    if (editTime && !opts.some((t) => t.time === editTime)) {
      opts.unshift({ time: editTime, afterHours: false });
    }
    return opts;
  }, [editAvailability, locationTz, editTime]);

  const [editExplorerOpen, setEditExplorerOpen] = useState(false);
  // Moves the explorer surfaced as part of a "Con redistribución" pick.
  // Held locally and only applied on Guardar (along with the table
  // change and any other field edits) so the operator can cancel out.
  const [editPendingMoves, setEditPendingMoves] = useState<
    Array<{
      reservation_id: string;
      tables: string[];
      // Optional display labels from the explorer pick. The save
      // payload strips them — the backend's ``TableMoveSchema`` is
      // ``extra='forbid'``.
      fromTableIds?: string[];
      guestName?: string;
      pax?: number;
    }>
  >([]);

  // The explorer plan in ``editPendingMoves`` is computed for a specific
  // (pax, date, time, zone) tuple. If the operator tweaks any of those
  // *after* picking, the plan is stale — the displaced reservations may
  // no longer overlap, or the target mesas may now be busy at the
  // displaced slot. ``movesTuple`` snapshots the tuple at pick time;
  // when it diverges from the current state, we drop the moves so save
  // doesn't apply a redistribute the solver never would have proposed
  // for the new tuple. The operator can re-open the explorer for a
  // fresh pick.
  const movesTuple = useRef<string | null>(null);
  const currentTuple = `${editPax}|${editDate}|${editTime}|${editZone}`;
  useEffect(() => {
    if (!isEditing) return;
    if (editPendingMoves.length === 0) {
      movesTuple.current = null;
      return;
    }
    if (movesTuple.current === null) {
      movesTuple.current = currentTuple;
      return;
    }
    if (movesTuple.current !== currentTuple) {
      setEditPendingMoves([]);
      movesTuple.current = null;
    }
  }, [isEditing, editPendingMoves, currentTuple]);

  if (!r) {
    return null;
  }

  const close = () => {
    onClose();
    setHighlightedReservationId(null);
    setHighlightedTableIds([]);
    setIsEditing(false);
  };

  // Sort table names with natural (numeric-aware) order so "T10" doesn't
  // jump before "T2" and a multi-table assignment like 20+30+40 always
  // reads ascending in the MESA(S) chip strip.
  const tableNameList = r.tableIds
    .map((id) => tables.find((t) => t.id === id)?.name || "Mesa")
    .toSorted((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
  const tableName = tableNameList.join(" + ");
  // Optimistic label for the in-flight move. Falls back to ``tableName``
  // (current/landed assignment) once the refetch confirms the move.
  const pendingTableName =
    pendingTableIds?.map((id) => tables.find((t) => t.id === id)?.name || "Mesa").join(" + ") ??
    null;
  const zoneName = r.preferredZone
    ? zones.find((z) => z.id === r.preferredZone)?.name || r.preferredZone
    : "";
  // Zona efectiva para mostrar en el bloque Reserva — en edit mode
  // usa el draft (``editZone`` se auto-sincroniza desde el explorer
  // cuando el operador pickea una mesa), en read mode la guardada.
  const effectiveZoneName = isEditing
    ? editZone
      ? (zones.find((z) => z.id === editZone)?.name ?? editZone)
      : ""
    : zoneName;
  // Tint del chip de mesa en el detalle — mismo accent de zona que el
  // mapa, el timeline y los chips del listado. Se resuelve desde la
  // zona real de la mesa asignada (con fallback a la zona preferida).
  const detailZone =
    zones.find((z) => z.id === tables.find((t) => t.id === r.tableIds[0])?.zoneId) ??
    zones.find((z) => z.id === r.preferredZone);
  const detailChipBg = detailZone ? zoneAccent(detailZone).fill : "#ededea";

  const startEdit = () => {
    setEditPax(r.pax);
    setEditDate(r.date);
    setEditZone(r.preferredZone || "");
    setEditTableIds([...r.tableIds]);
    setEditTime(r.time);
    setEditNotes(r.notes || "");
    setEditName(r.guestName);
    setEditPhone(r.guestPhone);
    setEditEmail(r.guestEmail);
    setEditFeatures([...(r.features ?? [])] as ApiReservationFeature[]);
    setEditTags([...(r.tags ?? [])] as ApiReservationTag[]);
    setEditIsVip(r.isVip ?? false);
    // Si había una barra de confirm abierta (Cancelar / Confirmar)
    // sin respuesta, dismissear al entrar a editar — el operador ya
    // dejó esa intención atrás. Si no, la barra reaparece al
    // volver de Editar y queda colgada en estado fantasma.
    setHeaderConfirm(null);
    setIsEditing(true);
  };

  const saveEdit = async () => {
    const payload: Record<string, unknown> = {};
    if (editPax !== r.pax) payload.pax = editPax;
    if (editZone !== (r.preferredZone || "")) payload.preferred_zone_id = editZone || null;
    // ``editNotes`` siempre representó las notas del cliente
    // (seedeadas de ``r.notes``); antes por un bug el form las
    // guardaba a ``internal_notes`` y eso duplicaba con el bloque
    // siempre-visible de Notas internas. Ahora persiste donde
    // corresponde — al campo ``notes`` (lo que escribió el comensal).
    if (editNotes !== (r.notes || "")) payload.notes = editNotes;
    if (editName.trim() !== r.guestName.trim() && editName.trim().length > 0) {
      payload.guest_name = editName.trim();
    }
    if (editPhone.trim() !== r.guestPhone.trim() && editPhone.trim().length > 0) {
      // Phone is the dedup key — backend will re-link the reservation
      // to a different Guest if this number already exists in the CRM.
      payload.guest_phone = editPhone.trim();
    }
    // Backend's ``_normalize_invitee_email`` lowercases the stored
    // value, so compare normalized to avoid a no-op PATCH when the
    // operator only flips the casing of an already-stored email.
    if (editEmail.trim().toLowerCase() !== r.guestEmail.trim().toLowerCase()) {
      payload.guest_email = editEmail.trim();
    }
    const oldFeaturesStr = [...r.features].toSorted().join(",");
    const newFeaturesStr = [...editFeatures].toSorted().join(",");
    if (oldFeaturesStr !== newFeaturesStr) payload.features = editFeatures;
    if (editStartsAt && editEndsAt && (editDate !== r.date || editTime !== r.time)) {
      payload.starts_at = editStartsAt;
      payload.ends_at = editEndsAt;
    }
    const oldIds = [...r.tableIds].toSorted().join(",");
    const newIds = [...editTableIds].toSorted().join(",");
    if (newIds !== oldIds) {
      if (r.status !== "sentado" && r.origin !== "walkin") {
        const { tables: allTables, zones: allZones } = useAppStore.getState();
        if (!(await confirmReservationTables(editTableIds, allTables, allZones))) return;
      }
      payload.tables = editTableIds;
    }
    const oldFeatures = [...((r.features as ApiTableFeature[]) ?? [])].toSorted().join(",");
    const newFeatures = [...editFeatures].toSorted().join(",");
    if (oldFeatures !== newFeatures) payload.features = editFeatures;
    // Mandar `[]` explícitamente cuando el operador destildó todo es el
    // punto del editor: el backend usa ``exclude_unset``, así que omitir
    // la clave dejaría el tag equivocado en la reserva.
    const oldTags = [...r.tags].toSorted().join(",");
    const newTags = [...editTags].toSorted().join(",");
    if (oldTags !== newTags) payload.tags = editTags;
    if (editIsVip !== r.isVip) payload.is_vip = editIsVip;
    if (editPendingMoves.length > 0) {
      // Strip display-only fields — backend's TableMoveSchema is extra="forbid".
      payload.moves = editPendingMoves.map((mv) => ({
        reservation_id: mv.reservation_id,
        tables: mv.tables,
      }));
    }

    if (Object.keys(payload).length === 0) {
      // Nothing changed in the form, but the dry-run banner may still
      // be flagging an unresolved constraint (e.g. the assigned table
      // doesn't carry the requested features). Without forcing a PATCH
      // the operator would silently exit edit-mode and never see the
      // override confirm dialog. Resend the current selection so the
      // backend re-runs ``_collect_table_constraint_issues`` and either
      // succeeds quietly or surfaces the override flow.
      if (dryRunQuery.data?.would_succeed === false) {
        payload.tables = editTableIds;
        payload.features = editFeatures;
      } else {
        setIsEditing(false);
        return;
      }
    }

    try {
      await apiUpdateReservation(r.id, payload);
      if (payload.tables) setFocusTableIds(editTableIds);
      // Follow the reservation when its date changed — otherwise the
      // detail panel goes blank because ``useTodayReservationsAll``
      // filters by ``selectedDate`` and the booking just moved off it.
      if (editDate !== r.date) {
        useAppStore.getState().setSelectedDate(editDate);
      }
      notify(
        "success",
        "Reserva actualizada",
        `${r.guestName} · ${editPax}p`,
        r.id,
        <PenLine size={18} strokeWidth={2.5} />,
      );
      setEditPendingMoves([]);
      setIsEditing(false);
    } catch {
      // ``apiUpdateReservation`` already surfaced the error toast via
      // onError — keep edit mode open so the operator can adjust.
    }
  };

  // Cuenta cuántos campos del form están "sucios" (distintos del valor
  // actual de ``r``). Cada campo cuenta como 1 — fecha y hora son dos.
  // Alimenta el contador "N cambios sin guardar" del footer.
  const dirtyCount = (() => {
    if (!isEditing) return 0;
    let n = 0;
    if (editPax !== r.pax) n++;
    if (editDate !== r.date) n++;
    if (editTime !== r.time) n++;
    if (editZone !== (r.preferredZone || "")) n++;
    const oldMesas = [...r.tableIds].toSorted().join(",");
    const newMesas = [...editTableIds].toSorted().join(",");
    if (oldMesas !== newMesas) n++;
    if (editNotes !== (r.notes || "")) n++;
    if (editName.trim() !== r.guestName.trim()) n++;
    if (editPhone.trim() !== r.guestPhone.trim()) n++;
    if (editEmail.trim().toLowerCase() !== r.guestEmail.trim().toLowerCase()) n++;
    const oldFeatures = [...r.features].toSorted().join(",");
    const newFeatures = [...editFeatures].toSorted().join(",");
    if (oldFeatures !== newFeatures) n++;
    const oldTags = [...r.tags].toSorted().join(",");
    const newTags = [...editTags].toSorted().join(",");
    if (oldTags !== newTags) n++;
    if (editIsVip !== r.isVip) n++;
    return n;
  })();

  // Validación liviana antes del save — el backend hace la
  // canónica (phonenumbers + email validator), acá solo bloqueamos
  // payloads obviamente rotos para evitar 422s y dar feedback
  // inmediato en el input.
  const phoneIsValid = (() => {
    const trimmed = editPhone.trim();
    if (trimmed.length === 0) return true;
    const digits = trimmed.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15;
  })();
  const emailIsValid = (() => {
    const trimmed = editEmail.trim();
    if (trimmed.length === 0) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  })();
  // editTime queda "" cuando el operador cambia la fecha (lo
  // forzamos a re-elegir hora del nuevo día). Solo aplica en
  // Reservada — Sentada y los terminales no editan hora.
  const timeIsValid = !isEditing || r.status !== "reservada" || editTime !== "";
  const editIsValid = phoneIsValid && emailIsValid && timeIsValid;

  const cancelEdit = () => {
    setEditPendingMoves([]);
    setIsEditing(false);
  };

  // ── New-design helpers ────────────────────────────────────────────
  const isBooked = r.status === "reservada";
  const isSeated = r.status === "sentado";
  const isNoShow = r.status === "noshow";
  const isCancelled = r.status === "cancelada";
  const isCompleted = r.status === "completada";
  const isTerminal = isCompleted || isCancelled || isNoShow;
  // En no-show y cancelada el backend solo acepta editar identidad
  // (nombre / tel / email). En esos estados ocultamos affordances de
  // los campos de Reserva (pax, fecha, hora, mesa, zona) para que el
  // host no intente guardar algo que el backend va a rechazar.
  const isIdentityOnlyEdit = isEditing && (isNoShow || isCancelled);
  // The host counts inside pax, so pax - 1 is what the invitee list can fill.
  const shownPax = isEditing && !isIdentityOnlyEdit ? editPax : r.pax;
  const inviteeCount = invitees?.length ?? 0;
  const inviteesOverPax = inviteeCount > Math.max(0, shownPax - 1);
  const hasPet = r.features.includes("pet_friendly");
  const hasAccessible = r.features.includes("accessible");
  // RIESGO comes from the backend now (excluding self for no-show rows).
  const riskFlag = r.guestIsRisk ?? false;

  // Confirmation URL — built off the per-booking token (the same one
  // the guest receives in their email).
  // TODO(multi-tenant): the host is hard-coded to ``seimseim.com``,
  // which is wrong for any org other than Seim·Seim. Revisit when a
  // second tenant goes live — either derive the host from the active
  // organization/location, or expose a backend-built URL alongside the
  // token so the panel doesn't reconstruct it. Flagged in PR #376 by
  // @avarajar; deferred deliberately while only one tenant is active.
  // ``manageUrl`` is built server-side per org from
  // ``OrganizationSettings.reservas_site_url`` (with a fallback to the
  // tenant's primary domain). When the backend can't resolve a base
  // (no settings + no primary domain), the chip is hidden entirely —
  // showing a broken / wrong host would be more confusing than
  // omitting the section.
  const confirmationFullUrl = r.manageUrl || "";
  const [, hourMinPartRaw, ampm] = /^(\d{1,2}:\d{2})\s*([AP]M)$/.exec(r.time) ?? ["", r.time, ""];
  const hourMinPart = hourMinPartRaw;
  const displayDate = dayjs(r.date).format("dddd · D MMM YYYY").toLowerCase();

  const minutesSeated = isSeated && r.seatedAt ? minutesSinceSeated(r.seatedAt, r.date) : null;
  const minutesSeatedDisplay =
    minutesSeated === null
      ? ""
      : minutesSeated < 60
        ? `${minutesSeated} min`
        : `${Math.floor(minutesSeated / 60)}h ${minutesSeated % 60}m`;

  const openWhatsApp = () => {
    const digits = r.guestPhone.replace(/\D/g, "");
    if (digits) window.open(`https://wa.me/${digits}`, "_blank", "noopener,noreferrer");
  };

  // "Compartir confirmación" → WhatsApp with the manage link prefilled.
  // Falls back to a recipient-less wa.me share sheet when the guest has
  // no phone on file so the host can still pick a chat.
  const shareConfirmationWhatsApp = () => {
    if (!confirmationFullUrl) return;
    const digits = r.guestPhone.replace(/\D/g, "");
    const text = encodeURIComponent(
      `Hola ${r.guestName}, aquí está el enlace de tu reserva: ${confirmationFullUrl}`,
    );
    const url = digits ? `https://wa.me/${digits}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setActionsMenuOpen(false);
  };

  const handleNotesBlur = async () => {
    const next = notesDraft.trim();
    const current = (r.internalNotes ?? "").trim();
    if (next === current) return;
    try {
      // Notas internas guardan inline — la propia notificación local
      // ya cubre el caso de fallo, no queremos el toast global encima.
      await apiUpdateReservation(r.id, { internal_notes: next }, { silentErrors: true });
    } catch (e) {
      const msg =
        readErrorDetail(e) ??
        (e && typeof e === "object" && "message" in e
          ? String((e as { message: unknown }).message)
          : "No se pudieron guardar las notas");
      notify("warning", "Notas no guardadas", msg, r.id);
      // Revert the draft so the user sees the saved value.
      setNotesDraft(current);
    }
  };

  const changePin = async (level: ReservationPinLevel) => {
    if (level === r.pinLevel || pinBusy !== null) return;
    setPinBusy(level);
    setPinError(null);
    try {
      await apiSetPinLevel(r.id, level, { silentErrors: true });
    } catch (e) {
      setPinError(readErrorDetail(e) ?? "No se pudo cambiar el reacomodo automático.");
    } finally {
      setPinBusy(null);
    }
  };

  const handleMoverClick = () => {
    if (isSeated) {
      // Para reservas ya sentadas, modal de confirm antes de abrir el
      // picker — la hostess está a punto de pedirle a comensales reales
      // que se cambien físicamente de mesa.
      setMoverSeatedConfirm(true);
      return;
    }
    setExplorerMode("reassign");
  };

  // Reverse the events from useReservationEvents — backend returns
  // newest-first by default? We sort to be sure and surface as a
  // chronological-ish timeline grouped by relative day.
  const reservaEvents = (reservationEvents ?? [])
    .slice()
    .toSorted((a, b) => b.timestamp - a.timestamp);

  return (
    <div className="flex flex-col h-full">
      {/* Header: Volver + pills de acción (Editar / Confirmar / Cancelar).
          Reemplaza al ⋯ — las 3 acciones que más se usan son visibles a
          primera vista. La barra de confirm aparece debajo, ancho
          completo, para que Sí / No sean targets grandes. */}
      <div className="flex items-center gap-2 border-b border-border px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={isEditing ? cancelEdit : close}
          className="flex items-center gap-1 text-text-primary font-semibold"
        >
          <ChevronLeft className="size-4" />
          Volver
        </Button>
        {!isEditing ? (
          <div className="ml-auto flex items-center gap-2">
            {/* COMPLETED queda totalmente bloqueada en backend; no
                tiene sentido mostrar el botón. NO_SHOW y CANCELLED
                aceptan identity-only edits (ver
                ``update_reservation`` view), así que el pill sigue
                disponible — el panel restringe los campos visibles. */}
            {!isCompleted && (
              <button
                type="button"
                onClick={startEdit}
                className="inline-flex items-center gap-1 rounded-full border border-text-primary bg-white px-3 py-1 text-[12px] font-bold text-text-primary hover:bg-[#f7f6f3]"
              >
                <PenLine className="size-3" />
                Editar
              </button>
            )}
            {/* ⋯ — Confirmar / Compartir / Cancelar. Lo que NO es un modo
                (Editar) ni inline vive aquí, como en el handoff Idea A.
                Confirmar y Cancelar disparan la barra de confirm inline. */}
            {(isBooked || isSeated || !!confirmationFullUrl) && (
              <Popover open={actionsMenuOpen} onOpenChange={setActionsMenuOpen}>
                <PopoverTrigger
                  aria-label="Más acciones"
                  className="inline-flex size-8 items-center justify-center rounded-lg border border-text-primary bg-text-primary text-white outline-none hover:opacity-90"
                >
                  <MoreHorizontal className="size-4" />
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={6} className="w-[228px] p-1.5">
                  {!!confirmationFullUrl && (
                    <>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(confirmationFullUrl, "share")}
                        className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[14px] font-medium text-text-primary hover:bg-[#f7f6f3]"
                      >
                        <Link2 className="size-4 shrink-0 text-text-secondary" />
                        {copiedKey === "share" ? "Link copiado ✓" : "Compartir confirmación"}
                      </button>
                      <button
                        type="button"
                        onClick={shareConfirmationWhatsApp}
                        className="block w-full pl-[37px] pr-2.5 pb-1.5 text-left font-mono text-[9px] uppercase tracking-wide text-text-muted hover:text-text-secondary"
                      >
                        copia el link · o envía por WhatsApp
                      </button>
                    </>
                  )}
                  {(isBooked || isSeated) && (
                    <button
                      type="button"
                      onClick={() => {
                        setActionsMenuOpen(false);
                        setHeaderConfirm("confirm");
                      }}
                      className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[14px] font-bold text-green-700 hover:bg-[#f7f6f3]"
                    >
                      <Check className="size-4 shrink-0" />
                      {r.isConfirmedByHost ? "Quitar confirmación" : "Confirmar reserva"}
                    </button>
                  )}
                  {isBooked && (
                    <>
                      <div className="mx-1.5 my-1 h-px bg-border" />
                      <button
                        type="button"
                        onClick={() => {
                          setActionsMenuOpen(false);
                          setHeaderConfirm("cancel");
                        }}
                        className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[14px] font-bold text-red-600 hover:bg-red-50"
                      >
                        <X className="size-4 shrink-0" />
                        Cancelar reserva
                      </button>
                    </>
                  )}
                </PopoverContent>
              </Popover>
            )}
          </div>
        ) : (
          // Indicador estático — el rediseño de Editar mete los affordances
          // de edición directamente en los campos del read mode, no en
          // botones aparte. Este pill solo recuerda al host que está en
          // modo edición. Salir = "Volver".
          <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-text-primary bg-white px-2.5 py-0.5 text-[11.5px] font-semibold text-text-primary">
            <PenLine className="size-3" />
            Editando
          </span>
        )}
      </div>

      {/* Inline confirm bar — ancho completo, justo debajo del header */}
      {headerConfirm && !isEditing && (
        <div
          className={
            "flex items-center justify-between gap-3 border-b px-5 py-3 " +
            (headerConfirm === "cancel"
              ? "border-red-200 bg-red-50"
              : "border-green-200 bg-green-50")
          }
        >
          <span
            className={
              "text-[14px] font-semibold " +
              (headerConfirm === "cancel" ? "text-red-700" : "text-green-800")
            }
          >
            {headerConfirm === "cancel"
              ? "¿Cancelar reserva?"
              : r.isConfirmedByHost
                ? "¿Quitar la confirmación?"
                : "¿Confirmar reserva?"}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                if (headerConfirm === "cancel") {
                  cancelReservation(r.id, "Cancelado por staff");
                  setHeaderConfirm(null);
                  close();
                } else {
                  toggleConfirmedByHost(r.id);
                  setHeaderConfirm(null);
                }
              }}
              className={
                "rounded-md px-3 py-1.5 text-[13px] font-semibold text-white " +
                (headerConfirm === "cancel"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-green-600 hover:bg-green-700")
              }
            >
              {headerConfirm === "cancel"
                ? "Sí, cancelar"
                : r.isConfirmedByHost
                  ? "Sí, quitar"
                  : "Sí, confirmar"}
            </button>
            <button
              type="button"
              onClick={() => setHeaderConfirm(null)}
              className={
                "rounded-md border bg-white px-3 py-1.5 text-[13px] font-medium " +
                (headerConfirm === "cancel"
                  ? "border-red-300 text-red-700 hover:bg-red-50"
                  : "border-green-300 text-green-700 hover:bg-green-50")
              }
            >
              No
            </button>
          </div>
        </div>
      )}

      {/* Edit dirty bar — mismo lugar que la barra de confirm, justo
          debajo del header. Solo aparece cuando hay cambios pendientes
          para no añadir ruido al entrar al modo edición. Ámbar para
          señal "warning, no destructivo". */}
      {isEditing && dirtyCount > 0 && (
        <div className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-5 py-3">
          <span className="text-[14px] font-semibold text-amber-800">
            <strong className="font-bold">{dirtyCount}</strong>{" "}
            {dirtyCount === 1 ? "cambio" : "cambios"} sin guardar
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={saveEdit}
              disabled={editTableIds.length === 0 || dirtyCount === 0 || !editIsValid}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-[13px] font-medium text-amber-800 hover:bg-amber-50"
            >
              Descartar
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
        {/* Cliente — siempre visible. En edit mode el nombre es input
            inline (mismo tamaño y peso que la versión read). Tel y
            email se mantienen como display por ahora; pasarlos a
            editable requiere extender ReservationUpdateSchema en el
            backend (issue de seguimiento). */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2 text-[18px] font-extrabold tracking-tight leading-tight">
            {isEditing ? (
              <label className="group relative inline-flex min-w-[60%] items-center">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nombre"
                  className="w-full rounded-md border border-dashed border-text-secondary/60 bg-white px-2 py-0.5 text-[18px] font-extrabold tracking-tight leading-tight text-text-primary outline-none focus:border-solid focus:border-text-primary"
                  aria-label="Editar nombre"
                />
                <PenLine className="pointer-events-none absolute right-2 size-3.5 text-text-secondary/70" />
              </label>
            ) : (
              <span>{r.guestName}</span>
            )}
            <span className="inline-flex items-center gap-1.5">
              {/* En edit (no identity-only) los flags de perrito y
                  silla son toggleables: seteado = chip sólido, no
                  seteado = chip con borde dashed + ``+`` para
                  agregarlo. En read mode solo se ven los seteados,
                  como antes. */}
              {isEditing && !isIdentityOnlyEdit ? (
                <>
                  <FeatureToggleChip
                    selected={editFeatures.includes("pet_friendly")}
                    onClick={() =>
                      setEditFeatures((prev) =>
                        prev.includes("pet_friendly")
                          ? prev.filter((f) => f !== "pet_friendly")
                          : [...prev, "pet_friendly"],
                      )
                    }
                    label="Mascota"
                    icon={<Dog className="size-3" />}
                  />
                  <FeatureToggleChip
                    selected={editFeatures.includes("accessible")}
                    onClick={() =>
                      setEditFeatures((prev) =>
                        prev.includes("accessible")
                          ? prev.filter((f) => f !== "accessible")
                          : [...prev, "accessible"],
                      )
                    }
                    label="Accesibilidad"
                    icon={<Accessibility className="size-3" />}
                  />
                  <FeatureToggleChip
                    selected={editIsVip}
                    onClick={() => setEditIsVip((prev) => !prev)}
                    label="VIP"
                    icon={
                      <Star
                        className={"size-3 " + (editIsVip ? "text-orange-500" : "")}
                        fill={editIsVip ? "currentColor" : "none"}
                      />
                    }
                  />
                </>
              ) : (
                <>
                  {hasPet && (
                    <span
                      className="inline-flex h-5 w-5 items-center justify-center rounded border border-[#c8c8c2] bg-[#f7f6f3] text-text-secondary"
                      title="Mascota"
                    >
                      <Dog className="size-3" />
                    </span>
                  )}
                  {hasAccessible && (
                    <span
                      className="inline-flex h-5 w-5 items-center justify-center rounded border border-[#c8c8c2] bg-[#f7f6f3] text-text-secondary"
                      title="Accesibilidad"
                    >
                      <Accessibility className="size-3" />
                    </span>
                  )}
                  {r.isVip && (
                    <span
                      className="inline-flex h-5 w-5 items-center justify-center rounded border border-[#c8c8c2] bg-[#f7f6f3] text-orange-500"
                      title="VIP"
                    >
                      <Star className="size-3" fill="currentColor" />
                    </span>
                  )}
                </>
              )}
              {riskFlag && (
                <span
                  className="inline-flex h-[18px] items-center rounded bg-[#b13030] px-1.5 text-[9px] font-extrabold uppercase tracking-wide text-white"
                  title="Cliente con historial de no-show"
                >
                  RIESGO
                </span>
              )}
              {/* Historial del cliente de un vistazo, en el mismo
                  renglón del nombre. Solo lectura + solo cuando hay
                  guest enlazado. La pill de visitas se oculta en 0
                  (cliente nuevo); la de no-show solo si tiene. */}
              {!isEditing && guestStats && guestStats.completedCount > 0 && (
                <span className="inline-flex h-[20px] items-center gap-1 rounded-full border border-[#c8c8c2] bg-[#f7f6f3] px-2 font-mono text-[10px] font-bold tracking-wide text-text-secondary">
                  <Clock className="size-2.5" />
                  <span className="text-text-primary">{guestStats.completedCount}</span>
                  {guestStats.completedCount === 1 ? "visita" : "visitas"}
                </span>
              )}
              {!isEditing && guestStats && guestStats.noShowCount > 0 && (
                <span className="inline-flex h-[20px] items-center rounded-full border border-[#e7b8b8] bg-[#fcebeb] px-2 font-mono text-[10px] font-bold tracking-wide text-[#b13030]">
                  {guestStats.noShowCount} no-show
                </span>
              )}
            </span>
          </div>

          {(isEditing || r.guestPhone || r.guestEmail) && (
            <div className="rounded-lg border border-[#c8c8c2] bg-white overflow-hidden">
              {(isEditing || r.guestPhone) && (
                <div className="grid grid-cols-[22px_1fr_auto_auto] items-center gap-1.5 border-b border-[#ececea] px-2.5 py-1.5 text-[13px] last:border-b-0">
                  <span className="text-center font-mono text-[12px] text-text-secondary">☎</span>
                  {isEditing ? (
                    <input
                      type="tel"
                      inputMode="tel"
                      value={editPhone}
                      // Filtramos a caracteres válidos de un teléfono
                      // (dígitos, '+' inicial, espacios, guiones,
                      // paréntesis) en la propia onChange, así el input
                      // ni siquiera deja entrar letras.
                      onChange={(e) => setEditPhone(e.target.value.replace(/[^\d+\s\-()]/g, ""))}
                      placeholder="+57 300 000 0000"
                      aria-invalid={!phoneIsValid}
                      className={
                        "rounded border bg-white px-1.5 py-0.5 font-mono text-[12px] text-text-primary outline-none focus:border-solid " +
                        (phoneIsValid
                          ? "border-dashed border-text-secondary/60 focus:border-text-primary"
                          : "border-solid border-red-500 focus:border-red-600")
                      }
                      aria-label="Editar teléfono"
                    />
                  ) : (
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[12px]">
                      {r.guestPhone}
                    </span>
                  )}
                  {isEditing ? (
                    <>
                      <PenLine className="size-3 text-text-secondary/70" />
                      <span />
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={openWhatsApp}
                        disabled={!r.guestPhone}
                        className="flex h-6 w-6 items-center justify-center rounded hover:bg-[#e8f7ec] disabled:opacity-40"
                        aria-label="Enviar por WhatsApp"
                      >
                        <WhatsAppIcon />
                      </button>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(r.guestPhone, "phone")}
                        className="flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-gray-100 hover:text-text-primary"
                        aria-label={copiedKey === "phone" ? "Teléfono copiado" : "Copiar teléfono"}
                      >
                        {copiedKey === "phone" ? (
                          <Check className="size-3.5 text-green-600" />
                        ) : (
                          <Copy className="size-3.5" />
                        )}
                      </button>
                    </>
                  )}
                </div>
              )}
              {(isEditing || r.guestEmail) && (
                <div className="grid grid-cols-[22px_1fr_auto_auto] items-center gap-1.5 px-2.5 py-1.5 text-[13px]">
                  <span className="text-center font-mono text-[12px] text-text-secondary">✉</span>
                  {isEditing ? (
                    <input
                      type="email"
                      inputMode="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      placeholder="nombre@correo.com"
                      aria-invalid={!emailIsValid}
                      className={
                        "rounded border bg-white px-1.5 py-0.5 text-[12.5px] text-text-primary outline-none focus:border-solid " +
                        (emailIsValid
                          ? "border-dashed border-text-secondary/60 focus:border-text-primary"
                          : "border-solid border-red-500 focus:border-red-600")
                      }
                      aria-label="Editar email"
                    />
                  ) : (
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px]">
                      {r.guestEmail}
                    </span>
                  )}
                  {isEditing ? (
                    <>
                      <PenLine className="size-3 text-text-secondary/70" />
                      <span />
                    </>
                  ) : (
                    <>
                      <span />
                      <button
                        type="button"
                        onClick={() => copyToClipboard(r.guestEmail, "email")}
                        className="flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-gray-100 hover:text-text-primary"
                        aria-label={copiedKey === "email" ? "Email copiado" : "Copiar email"}
                      >
                        {copiedKey === "email" ? (
                          <Check className="size-3.5 text-green-600" />
                        ) : (
                          <Copy className="size-3.5" />
                        )}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RESERVA — siempre visible. En edit mode el bloque mantiene
            su layout y la celda Personas gana un stepper inline; mesa,
            zona y link quedan ocultos para no duplicar con el form de
            abajo (los iremos lifteando campo por campo). */}
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Reserva</SectionLabel>
          <div className="rounded-[10px] border border-[#c8c8c2] bg-[#f7f6f3] overflow-hidden">
            <div className="p-3.5">
              {/* Date strip — editable inline en edit mode salvo en
                  SEATED, donde la reserva ya está sentada y mover la
                  fecha pierde sentido. El picker usa Calendar (mismo
                  componente que NewReservationModal) y bloquea días
                  pasados, weekdays sin opening hours y cierres
                  full-day vía isDateDisabled. */}
              {isEditing && !isSeated && !isIdentityOnlyEdit ? (
                <div className="mb-2.5 flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger className="inline-flex items-center gap-2 rounded-md border border-dashed border-text-secondary/60 bg-white px-3 py-1.5 text-[14px] font-semibold text-text-primary outline-none hover:bg-[#f7f6f3] focus:border-solid focus:border-text-primary">
                      {formatBookingDateLabel(editDate, locationTz)}
                      <CalendarIcon className="size-4 text-text-secondary/70" />
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dayjs(editDate).toDate()}
                        onSelect={(d) => {
                          if (!d) return;
                          const next = dayjs(d).format("YYYY-MM-DD");
                          if (next !== editDate) {
                            // Al cambiar la fecha, la hora original
                            // probablemente quede fuera de los slots
                            // del nuevo día. La blanqueamos para
                            // forzar al operador a elegir una hora
                            // válida — el botón Guardar se bloquea
                            // hasta que lo haga.
                            setEditTime("");
                          }
                          setEditDate(next);
                        }}
                        disabled={isDateDisabled}
                      />
                    </PopoverContent>
                  </Popover>
                  <PenLine className="size-3 text-text-secondary/70" />
                </div>
              ) : (
                <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-text-secondary">
                  {displayDate} · {r.time}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2.5">
                <div
                  className={
                    "rounded-lg border bg-white px-3 py-2 " +
                    (isEditing && !isSeated && !isIdentityOnlyEdit
                      ? "border-dashed border-text-secondary/60"
                      : "border-[#c8c8c2]")
                  }
                >
                  <div className="mb-0.5 flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                    Hora
                    {isEditing && !isSeated && !isIdentityOnlyEdit && (
                      <PenLine className="size-2.5" />
                    )}
                  </div>
                  {isEditing && !isSeated && !isIdentityOnlyEdit ? (
                    (loadingEditAvailability && editAvailabilityTimes.length === 0) ||
                    editAvailabilityIsStale ? (
                      <span className="text-[12px] text-text-secondary">Cargando…</span>
                    ) : editAvailabilityTimes.length === 0 ? (
                      <span className="text-[12px] text-text-secondary">Sin horario</span>
                    ) : (
                      <select
                        value={editTime}
                        onChange={(e) => setEditTime(e.target.value)}
                        aria-invalid={editTime === ""}
                        className={
                          "w-full bg-transparent font-mono text-[18px] font-extrabold leading-none outline-none " +
                          (editTime === "" ? "text-red-600" : "")
                        }
                      >
                        {/* Placeholder visible cuando la hora quedó
                            blanqueada (típicamente al cambiar la
                            fecha). Forzamos al operador a elegir un
                            slot real antes de poder guardar. */}
                        {editTime === "" && (
                          <option value="" disabled>
                            Elige hora
                          </option>
                        )}
                        {editAvailabilityTimes.map((t) => (
                          <option key={t.time} value={t.time}>
                            {t.time}
                            {t.afterHours ? " · Solo staff" : ""}
                          </option>
                        ))}
                      </select>
                    )
                  ) : (
                    <div className="font-mono text-[22px] font-extrabold leading-none">
                      {hourMinPart}
                      {ampm && (
                        <span className="ml-0.5 text-[11px] font-medium tracking-wider text-text-secondary">
                          {ampm}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div
                  className={
                    "rounded-lg border bg-white px-3 py-2 " +
                    (isEditing && !isIdentityOnlyEdit
                      ? "border-dashed border-text-secondary/60"
                      : "border-[#c8c8c2]")
                  }
                >
                  <div className="mb-0.5 flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                    Personas
                    {isEditing && !isIdentityOnlyEdit && <PenLine className="size-2.5" />}
                  </div>
                  {isEditing && !isIdentityOnlyEdit ? (
                    <div className="flex items-center justify-between gap-1">
                      <button
                        type="button"
                        onClick={() => setEditPax((p) => Math.max(1, p - 1))}
                        className="flex h-7 w-7 items-center justify-center rounded border border-[#c8c8c2] bg-white text-[16px] font-bold text-text-primary hover:bg-[#f7f6f3]"
                        aria-label="Quitar una persona"
                      >
                        −
                      </button>
                      <span className="font-mono text-[22px] font-extrabold leading-none text-text-primary">
                        {editPax}
                        <span className="ml-0.5 text-[11px] font-medium tracking-wider text-text-secondary">
                          pax
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditPax((p) => p + 1)}
                        className="flex h-7 w-7 items-center justify-center rounded border border-[#c8c8c2] bg-white text-[16px] font-bold text-text-primary hover:bg-[#f7f6f3]"
                        aria-label="Agregar una persona"
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <div className="font-mono text-[22px] font-extrabold leading-none">
                      {r.pax}
                      <span className="ml-0.5 text-[11px] font-medium tracking-wider text-text-secondary">
                        pax
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {inviteesOverPax && (
                <p className="mt-2 text-[11px] leading-snug text-rose-700">
                  {inviteeCount} {inviteeCount === 1 ? "invitado" : "invitados"} para {shownPax}{" "}
                  pax.
                </p>
              )}

              {/* MESA + ZONA — en read mode son cajas de display; en
                  edit Reservada la mesa es un botón que abre el
                  TableExplorer staged (``setEditExplorerOpen``). En
                  Sentada midservice no se puede diferir hasta el
                  Guardar (liberar+ocupar tiene que ser atómico), así
                  que el click cierra el Editar y dispara el flujo
                  Mover mesa del read mode — ese ya está optimizado
                  para midservice y lo respetamos tal cual. */}
              {isEditing && !isIdentityOnlyEdit && (
                <div className="mt-2.5 grid grid-cols-[1fr_auto] items-stretch gap-2">
                  {!isSeated ? (
                    <button
                      type="button"
                      onClick={() => setEditExplorerOpen(true)}
                      className="flex flex-col gap-1.5 rounded-lg border border-dashed border-text-secondary/60 bg-white px-3 py-2 text-left hover:bg-[#f7f6f3]"
                    >
                      <span className="inline-flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                        {editTableIds.length > 1 ? "Mesas" : "Mesa"}
                        <PenLine className="size-2.5" />
                      </span>
                      {editTableIds.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {editTableIds
                            .map((id) => ({
                              id,
                              name: tables.find((tb) => tb.id === id)?.name || "Mesa",
                            }))
                            .toSorted((a, b) => compareTableNames(a.name, b.name))
                            .map(({ id, name }) => (
                              <span
                                key={id}
                                className="rounded bg-black px-2 py-0.5 text-[13px] font-extrabold leading-none text-white"
                              >
                                {name}
                              </span>
                            ))}
                        </div>
                      ) : (
                        <span className="text-[12px] text-text-secondary">Sin mesa</span>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        cancelEdit();
                        handleMoverClick();
                      }}
                      title="Mover mesa midservice"
                      className="flex flex-col gap-1.5 rounded-lg border border-dashed border-text-secondary/60 bg-white px-3 py-2 text-left hover:bg-[#f7f6f3]"
                    >
                      <span className="inline-flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                        {tableNameList.length > 1 ? "Mesas" : "Mesa"}
                        <PenLine className="size-2.5" />
                      </span>
                      {tableNameList.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {tableNameList.map((n, i) => (
                            <span
                              key={`${n}-${i}`}
                              className="rounded bg-black px-2 py-0.5 text-[13px] font-extrabold leading-none text-white"
                            >
                              {n}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[12px] text-text-secondary">Sin mesa</span>
                      )}
                    </button>
                  )}

                  {/* Zona — display-only siempre. En edit mode no la
                    editamos directamente: el TableExplorer ya auto-
                    sincroniza ``editZone`` con la zona de la mesa
                    pickeada. Acá solo se refleja para que el
                    operador vea hacia qué zona va. Las restricciones
                    de mesa por features (perrito / silla) ya las
                    aplica el backend a través de ``features`` en el
                    picker. */}
                  {effectiveZoneName ? (
                    <div className="flex min-w-[110px] flex-col gap-0.5 rounded-lg border border-[#c8c8c2] bg-white px-3 py-2">
                      <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                        Zona
                      </span>
                      <span className="text-[14px] font-bold leading-tight">
                        {effectiveZoneName}
                      </span>
                    </div>
                  ) : (
                    <div className="flex min-w-[110px] flex-col gap-0.5 rounded-lg border border-dashed border-[#c8c8c2] bg-white px-3 py-2">
                      <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                        Zona
                      </span>
                      <span className="text-[12px] text-text-secondary leading-tight">
                        Sin zona
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Dry-run banner — solo en edit. Vivía dentro del
                  widget Mesa del form viejo; ahora que la mesa es
                  inline en el bloque Reserva, este banner sigue
                  pegado a la mesa pero sin enmarcarla. */}
              {isEditing && !isIdentityOnlyEdit && (
                <div className="mt-2">
                  <EditDryRunBanner
                    query={dryRunQuery}
                    hasTables={editTableIds.length > 0}
                    hasPendingMoves={editPendingMoves.length > 0}
                    wasteHint={wasteHint}
                    wasteSuggestions={wasteSuggestions}
                    pickedCount={editTableIds.length}
                    editPax={editPax}
                    onApplySubset={(ids) => setEditTableIds(ids)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* MESA · ZONA — bloque unificado (handoff Idea A): chips +
            ↔ Mover protagonista arriba; tira gris "¿reubicable?" +
            texto que explica la opción activa, abajo. Solo lectura —
            el picker staged de edición vive en el bloque Reserva. */}
        {!isEditing && (
          <div className="flex flex-col gap-1.5">
            <SectionLabel>
              {effectiveZoneName ? `Mesa · ${effectiveZoneName}` : "Mesa"}
            </SectionLabel>
            <div className="overflow-hidden rounded-[10px] border border-[#111] bg-white">
              <div className="flex items-center justify-between gap-2.5 px-3 py-3">
                <div className="flex flex-wrap gap-1">
                  {pendingTableName ? (
                    <span className="inline-flex items-center gap-1.5 text-[12px] text-text-secondary">
                      <Spinner className="size-3" /> Moviendo a {pendingTableName}…
                    </span>
                  ) : tableNameList.length > 0 ? (
                    tableNameList.map((n, i) => (
                      <span
                        key={`${n}-${i}`}
                        style={{ backgroundColor: detailChipBg }}
                        className="rounded-md px-2.5 py-1.5 font-mono text-[15px] font-extrabold leading-none text-[#1a1a1a]"
                      >
                        {n}
                      </span>
                    ))
                  ) : (
                    <span className="text-[13px] text-text-secondary">Sin mesa</span>
                  )}
                </div>
                {!isTerminal && (
                  <button
                    type="button"
                    onClick={handleMoverClick}
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#111] bg-[#111] px-4 py-2.5 text-[14px] font-bold text-white hover:opacity-90"
                  >
                    ↔ Mover
                  </button>
                )}
              </div>
              {!isTerminal && (
                <div className="border-t border-[#ececea] bg-[#f7f6f3] px-3 py-2.5">
                  <div className="mb-1.5 font-mono text-[9px] font-bold uppercase tracking-wider text-text-secondary">
                    ¿El sistema puede reubicarla?
                  </div>
                  <div className="grid grid-cols-3 overflow-hidden rounded-md border border-[#c8c8c2] bg-white">
                    {PIN_OPTIONS.map((opt, i) => {
                      const active = r.pinLevel === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={pinBusy !== null}
                          onClick={() => changePin(opt.value)}
                          aria-pressed={active}
                          className={
                            "flex items-center justify-center gap-1 px-2 py-2 text-[12px] font-semibold transition disabled:opacity-50 " +
                            (i > 0 ? "border-l border-[#ececea] " : "") +
                            (active
                              ? "bg-black text-white"
                              : "bg-white text-text-secondary hover:bg-[#f7f6f3]")
                          }
                        >
                          {opt.icon}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {(() => {
                    const h = pinHelper(r.pinLevel, effectiveZoneName);
                    return (
                      <p className="mt-2 text-[11px] leading-snug text-text-secondary">
                        <b className="font-bold text-text-primary">{h.lead}</b>
                        {h.rest}
                      </p>
                    );
                  })()}
                  {pinError && (
                    <p className="mt-1 text-[11px] leading-snug text-amber-700">{pinError}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* NOTAS DEL CLIENTE — solo en read mode (en edit el form
            tiene su propio textarea). Fondo crema + cita en itálica
            para que la voz del comensal se sienta distinta del resto
            del panel. Renderiza solo si hay notas, tags u ocasión —
            el mismo criterio que enciende el ícono de "tiene nota"
            en la tarjeta del listado. */}
        {!isEditing && (r.notes || r.tags.length > 0 || (r.occasion && r.occasion !== "none")) && (
          <div className="flex flex-col gap-1.5">
            <div className="text-[13px] font-bold text-text-primary">Notas del cliente</div>
            <div className="rounded-lg border border-[#e8e2cc] bg-[#fdfbf2] px-3 py-2.5 leading-snug">
              {r.notes ? (
                <p className="text-[14px] italic leading-relaxed text-text-primary">{r.notes}</p>
              ) : (
                <span className="text-[13px] text-text-secondary">Sin notas del cliente</span>
              )}
              {(r.tags.length > 0 || (r.occasion && r.occasion !== "none")) && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {r.occasion && r.occasion !== "none" && (
                    <span className="rounded-full border border-[#e8e2cc] bg-white px-1.5 py-0.5 text-[10px] font-medium text-text-secondary">
                      {occasionLabels[r.occasion] ?? r.occasion}
                    </span>
                  )}
                  {r.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-[#e8e2cc] bg-white px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                    >
                      {reservationTagLabel(tag)}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-text-secondary">
                Origen · {originLabels[r.origin] ?? r.origin}
              </div>
            </div>
          </div>
        )}

        {/* NOTAS INTERNAS — solo visible en read mode. El editar tiene
            su propio textarea para Notas del cliente; las internas
            quedan aparte para que la operadora no las confunda con un
            campo del form. Autosave on blur. Backend acepta
            internal_notes incluso en terminal CANCELLED / NO_SHOW. */}
        {!isEditing &&
          (notesExpanded || notesDraft.trim() ? (
            <div className="flex flex-col gap-1.5">
              <SectionLabel>Notas internas</SectionLabel>
              <Textarea
                ref={notesTextareaRef}
                rows={2}
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                onBlur={() => {
                  handleNotesBlur();
                  if (!notesDraft.trim()) setNotesExpanded(false);
                }}
                placeholder="Sin notas del host…"
                className="resize-none rounded-lg border-dashed bg-white text-[13px] text-text-primary"
              />
            </div>
          ) : (
            // Colapsada: casi siempre vacías, así que por defecto solo
            // un affordance discreto. Tap → expande + enfoca.
            <button
              type="button"
              onClick={() => {
                setNotesExpanded(true);
                requestAnimationFrame(() => notesTextareaRef.current?.focus());
              }}
              className="flex items-center justify-between rounded-lg border border-dashed border-[#c8c8c2] bg-white px-3 py-2.5 text-left hover:bg-[#f7f6f3]"
            >
              <span className="inline-flex items-center gap-1.5 text-[13px] text-text-secondary">
                <MessageSquare className="size-3.5" />
                Notas internas
              </span>
              <span className="font-mono text-[12px] font-bold text-text-secondary">+ Agregar</span>
            </button>
          ))}

        {/* En no-show / cancelada solo se edita identidad arriba, así
            que ni el form ni el read mode renderizan acá — la pantalla
            queda con Cliente (editable) + Reserva (display) + Notas
            internas + Historial. Reservada y Sentada: form abajo.
            Resto: read mode. */}
        {isEditing && !isIdentityOnlyEdit ? (
          /* ── Edit mode — unified form (Reservada y Sentada) ── */
          <div className="flex flex-col gap-3">
            {/* Notas del cliente — editable. Lo que escribió el
                comensal al reservar; el operador puede ajustarlo
                cuando el cliente llama después con cambios. NO se
                confunde con Notas internas, que vive en su propio
                bloque siempre visible afuera del form. */}
            <div className="flex flex-col gap-1">
              <Label className="text-[11px] text-text-secondary">Notas del cliente</Label>
              <Textarea
                rows={2}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Lo que escribió el cliente al reservar…"
                className="text-xs resize-none"
              />
            </div>

            {/* Justo debajo de la nota porque el backend deriva estos
                tags de ese mismo texto al crear la reserva: el operador
                lee la nota y el chip equivocado de una sola pasada. */}
            <ReservationTagPicker value={editTags} onChange={setEditTags} />

            {/* Save / Cancel viven ahora en el footer pegado al pie
                del panel, fuera del scroll del contenido, para que
                Guardar siempre esté accesible sin importar dónde esté
                la hostess en el form. */}

            {/* Side-effect preview: ``editPendingMoves`` carries the
                relocations that the explorer's "Con redistribución"
                pick brought along. They only apply on Guardar (same
                transaction as the rest of the edit), but the operator
                deserves to see what's about to happen before
                confirming. */}
            {editPendingMoves.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-[11px] text-amber-900">
                <p className="font-semibold">Al guardar también se hará:</p>
                <ul className="mt-1 space-y-1">
                  {editPendingMoves.map((mv, i) => {
                    // Prefer the server-resolved labels (work even when
                    // the moved reservation isn't loaded in the local
                    // store — e.g. editing for a future date the
                    // operator hasn't navigated to). Fall back to the
                    // store lookup for legacy callers.
                    const fromStore = todayReservations.find((res) => res.id === mv.reservation_id);
                    const toLabel = mv.tables
                      .map((tid) => tables.find((t) => t.id === tid)?.name || "Mesa")
                      .join(" + ");
                    const fromIds = mv.fromTableIds ?? fromStore?.tableIds ?? [];
                    const fromLabel = fromIds.length
                      ? fromIds
                          .map((tid) => tables.find((t) => t.id === tid)?.name || "Mesa")
                          .join(" + ")
                      : "?";
                    const guestName = mv.guestName || fromStore?.guestName || "";
                    const pax = mv.pax || fromStore?.pax || 0;
                    const subject = guestName
                      ? `${guestName}${pax ? ` (${pax}p)` : ""}`
                      : "la reserva";
                    return (
                      <li key={i}>
                        ↻ Mover <span className="font-medium">{subject}</span> de{" "}
                        <span className="font-medium">{fromLabel}</span> a{" "}
                        <span className="font-medium">{toLabel}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Explorer drawer reusable: misma UX que "Cambiar de mesa".
                Aplica solo el pick a editTableIds, no toca el backend
                hasta que el operador haga Guardar. */}
            {editExplorerOpen && editStartsAt && editEndsAt && (
              <TableExplorer
                open
                onOpenChange={setEditExplorerOpen}
                context="reassign"
                pax={editPax}
                startsAt={editStartsAt}
                endsAt={editEndsAt}
                preferredZoneId={editZone || null}
                // Las features del draft filtran qué mesas surfaceea
                // el explorer (mascota → solo pet_friendly; silla →
                // solo accessible). Si el host marca esos chips en
                // el bloque Cliente, al abrir el picker solo verá
                // mesas compatibles. La zona del bloque Reserva es
                // derivada de la mesa, así que perder el popover
                // explícito no rompe el flujo.
                features={editFeatures}
                excludeReservationId={r.id}
                currentTableIds={editTableIds}
                zoneName={(id) => zonesData?.find((z) => z.id === id)?.name ?? "Zona"}
                onPick={(pick) => {
                  setEditTableIds(pick.tableIds);
                  setEditPendingMoves(pick.moves);
                  // Auto-sync editZone to the picked tables' zone so
                  // the backend's ``ensure_tables_zone_consistency``
                  // check doesn't fail at save time. ``pick.zoneId`` is
                  // the explorer's resolved zone for the group.
                  if (pick.zoneId && pick.zoneId !== editZone) {
                    setEditZone(pick.zoneId);
                  }
                  setEditExplorerOpen(false);
                }}
              />
            )}
          </div>
        ) : !isEditing ? (
          /* ── Read mode (new design) — solo cuando no estamos
              editando. En identity-only edit no se muestra ni el
              form ni el read mode: la pantalla queda con Cliente
              (editable) + Reserva (display) + Historial. ── */
          <>
            {/* Acción contextual al pie — Sentar vive en la tarjeta del
                listado, así que el detalle solo conserva las acciones
                que sí se disparan desde aquí: Finalizar (sentada) y
                Revertir (no-show). Mover + reubicar viven en el bloque
                "Mesa · Zona" de arriba; Retrasar se quitó por confuso. */}
            {isSeated && (
              <div className="flex flex-col gap-2">
                {!cerrarConfirm ? (
                  <button
                    type="button"
                    onClick={() => setCerrarConfirm(true)}
                    className="flex min-h-12 items-center justify-center gap-1.5 rounded-lg bg-black px-4 text-[14px] font-bold text-white hover:bg-black/90"
                  >
                    Finalizar visita
                  </button>
                ) : (
                  <div className="grid grid-cols-[1fr_auto] gap-1.5">
                    <button
                      type="button"
                      onClick={() => markCompleted(r.id)}
                      className="flex min-h-12 items-center justify-center rounded-lg bg-black px-3 text-[13px] font-bold text-white hover:bg-black/90"
                    >
                      Sí, finalizar {tableName}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCerrarConfirm(false)}
                      className="flex min-h-12 items-center justify-center rounded-lg border border-[#c8c8c2] bg-white px-3 text-[13px] font-medium text-text-secondary hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
                <div className="rounded-lg border border-dashed border-[#c8c8c2] bg-[#f7f6f3] px-3 py-2 text-[12px] leading-snug text-text-secondary">
                  {cerrarConfirm
                    ? "Liberará la mesa para próximas reservas."
                    : minutesSeatedDisplay
                      ? `Sentados hace ${minutesSeatedDisplay}.`
                      : "Sentados ya."}
                </div>
              </div>
            )}

            {isNoShow && (
              <button
                type="button"
                onClick={() => {
                  revertNoShow(r.id);
                  close();
                }}
                className={`flex min-h-12 w-full items-center justify-center gap-1.5 rounded-lg border px-4 text-[14px] font-bold ${NO_SHOW_DETAIL_CLASS}`}
              >
                <Undo2 className="size-4" />
                Revertir no-show / Sentar
              </button>
            )}

            {/* HISTORIAL · tabs Reserva (default) / Cliente */}
            <div className="flex flex-col gap-1.5">
              <SectionLabel>Historial</SectionLabel>
              <div className="flex gap-1 border-b border-[#c8c8c2]">
                <button
                  type="button"
                  onClick={() => setHistoryTab("reserva")}
                  className={
                    "-mb-px border-b-2 px-3 py-2 text-[12px] font-semibold transition " +
                    (historyTab === "reserva"
                      ? "border-black text-text-primary"
                      : "border-transparent text-text-secondary hover:text-text-primary")
                  }
                >
                  Reserva{" "}
                  <span className="ml-0.5 font-mono text-[10px] text-text-secondary">
                    {reservaEvents.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setHistoryTab("cliente")}
                  className={
                    "-mb-px border-b-2 px-3 py-2 text-[12px] font-semibold transition " +
                    (historyTab === "cliente"
                      ? "border-black text-text-primary"
                      : "border-transparent text-text-secondary hover:text-text-primary")
                  }
                >
                  Cliente
                </button>
              </div>
              {historyTab === "reserva" ? (
                <div className="flex flex-col">
                  {reservaEvents.length === 0 ? (
                    <div className="py-3 text-[11px] text-text-secondary">
                      Sin eventos en esta reserva todavía.
                    </div>
                  ) : (
                    (showAllReservaHistory ? reservaEvents : reservaEvents.slice(0, 5)).map(
                      (ev, i) => (
                        <div
                          key={i}
                          className="grid grid-cols-[78px_1fr] gap-2 border-b border-[#ececea] py-1.5 text-[12px] last:border-b-0"
                        >
                          <div className="font-mono text-[11px] text-text-secondary">
                            {formatHistoryWhen(ev.timestamp)}
                          </div>
                          <div className="leading-tight text-text-secondary">
                            {formatHistoryWhat(ev, r.origin)}
                            {ev.actorName && (
                              <span className="ml-1 text-[11px] text-text-secondary/70">
                                · por {ev.actorName}
                              </span>
                            )}
                          </div>
                        </div>
                      ),
                    )
                  )}
                  {reservaEvents.length > 5 && (
                    <button
                      type="button"
                      onClick={() => setShowAllReservaHistory((v) => !v)}
                      className="mt-1 self-start text-[11px] font-semibold text-text-secondary underline-offset-2 hover:text-text-primary hover:underline"
                    >
                      {showAllReservaHistory
                        ? "Ver menos"
                        : `Ver todo el historial · ${reservaEvents.length} eventos`}
                    </button>
                  )}
                </div>
              ) : (
                <div className="py-3 text-[11px] text-text-secondary">
                  Próximamente: visitas y no-shows del cliente en esta sede.
                </div>
              )}
            </div>
          </>
        ) : null}

        {/* Confirm modal · Mover en Sentada */}
        {moverSeatedConfirm && (
          <div
            className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 px-4"
            onClick={() => setMoverSeatedConfirm(false)}
          >
            <div
              className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-[15px] font-bold leading-tight">
                Reserva ya en {tableName || "esta mesa"}, ¿confirmas que los quieres mover?
              </h3>
              <p className="mt-2 text-[12px] leading-snug text-text-secondary">
                Estos comensales ya están sentados. Mover implica pedirles que se cambien de puesto.
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => setMoverSeatedConfirm(false)}
                  className="flex-1 rounded-lg border border-[#c8c8c2] bg-white px-3 py-2 text-[13px] font-medium text-text-secondary hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMoverSeatedConfirm(false);
                    setExplorerMode("mid-service");
                  }}
                  className="flex-1 rounded-lg bg-black px-3 py-2 text-[13px] font-bold text-white hover:bg-black/90"
                >
                  Mover
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {explorerMode !== null && (
        <TableExplorer
          open
          onOpenChange={(o) => !o && setExplorerMode(null)}
          context={explorerMode}
          pax={r.pax}
          startsAt={
            explorerMode === "seat-override"
              ? dayjs().toISOString()
              : parseInTz(`${r.date} ${r.time}`, "YYYY-MM-DD h:mm A", locationTz).toISOString()
          }
          endsAt={parseInTz(
            `${r.endDate ?? r.date} ${r.endTime}`,
            "YYYY-MM-DD h:mm A",
            locationTz,
          ).toISOString()}
          preferredZoneId={r.preferredZone || null}
          features={(r.features as string[]) ?? []}
          excludeReservationId={r.id}
          currentTableIds={r.tableIds}
          zoneName={(id) => zonesData?.find((z) => z.id === id)?.name ?? "Zona"}
          onPick={async (pick: TableExplorerPick) => {
            // Seating someone happens with them at the door — a walk-in in
            // all but name. Only a booking placed by hand asks first.
            if (
              explorerMode !== "seat-override" &&
              explorerMode !== "mid-service" &&
              r.origin !== "walkin"
            ) {
              const { tables: allTables, zones: allZones } = useAppStore.getState();
              if (!(await confirmReservationTables(pick.tableIds, allTables, allZones))) return;
            }
            setPendingTableIds(pick.tableIds);
            setExplorerMode(null);
            // Gate every flow that ships ``requires_moves`` behind an
            // explicit confirm dialog. The explorer enriches each move
            // with ``guestName`` / ``fromTableIds`` / ``pax`` so the
            // dialog can render the affected reservations by name; the
            // resolved tables are stripped to backend-shape inside the
            // apply call below.
            if (pick.moves.length > 0) {
              const { useMoveConfirmStore } = await import("@/store/move-confirm-store");
              const intent: "edit" | "seat" | "explorer" =
                explorerMode === "seat-override" || explorerMode === "mid-service"
                  ? "seat"
                  : "edit";
              const tableLabel = (ids: string[] | undefined) =>
                (ids ?? []).map((id) => tables.find((t) => t.id === id)?.name || "Mesa");
              const confirmed = await useMoveConfirmStore.getState().open({
                intent,
                moves: pick.moves.map((m) => ({
                  reservation_id: m.reservation_id,
                  guest_name: m.guestName || "Otra reserva",
                  pax: m.pax ?? 0,
                  from_table_names: tableLabel(m.fromTableIds),
                  to_table_names: tableLabel(m.tables),
                })),
              });
              if (!confirmed) {
                setPendingTableIds(null);
                return;
              }
            }
            try {
              if (explorerMode === "mid-service") {
                await performMidServiceMove(r, pick.tableIds, pick.moves);
              } else if (explorerMode === "seat-override") {
                await apiSeatReservation(r.id, {
                  tables: pick.tableIds,
                  moves: pick.moves,
                });
              } else {
                // Strip display-only fields from the move chain. The
                // explorer enriches each move with ``fromTableIds`` /
                // ``guestName`` / ``pax`` so the picker card and the
                // confirm dialog can render names — but the backend's
                // ``TableMoveSchema`` is ``extra='forbid'`` and 422s on
                // anything beyond ``reservation_id`` + ``tables``.
                await apiApplyPlan({
                  moves: pick.moves.map((m) => ({
                    reservation_id: m.reservation_id,
                    tables: m.tables,
                  })),
                  target_reservation_id: r.id,
                  target_tables: pick.tableIds,
                });
              }
              // Optimistic local patch so the canvas + sidebar reflect
              // the move without waiting for the refetch.
              useAppStore.setState((state) => ({
                reservations: state.reservations.map((res) =>
                  res.id === r.id ? { ...res, tableIds: pick.tableIds } : res,
                ),
              }));
              useAppStore.getState().setHighlightedTableIds(pick.tableIds);
              setFocusTableIds(pick.tableIds);
            } catch (e) {
              setPendingTableIds(null);
              console.error("explorer pick failed", e);
            }
          }}
        />
      )}
    </div>
  );
}

// ── Nueva reserva (in-sidebar surface) ──────────────────────────
//
// Replaces the standalone modal. Mirrors the DetailView layout so the
// operator learns one paradigm: Cliente block + Reserva block + Notas
// + footer actions. Draft state lives in Zustand (``newReservationDraft``)
// so accidental nav doesn't lose typed input.
//
// Phase 1 (current): the Cliente block, Reserva block sequencing, and
// submit are scaffolded across follow-up commits. Mesa selection uses
// the existing TableExplorer drawer for now; Phase 2 replaces it with
// canvas-plan picking.

function NewReservationView({ onClose }: { onClose: () => void }) {
  const draft = useAppStore((s) => s.newReservationDraft);
  const updateDraft = useAppStore((s) => s.updateNewReservationDraft);
  const resetDraft = useAppStore((s) => s.resetNewReservationDraft);
  const resetTick = useAppStore((s) => s.newReservationResetTick);
  const pendingSeed = useAppStore((s) => s.pendingNewReservationSeed);
  const clearPendingSeed = useAppStore((s) => s.clearPendingNewReservationSeed);
  const queryClient = useQueryClient();
  // Pull tables AND zones from the backend hooks (same source the
  // TableExplorer uses), so any id the explorer hands back on pick
  // always resolves to a name. The local Zustand copies lag behind
  // the backend after closures/edits and used to surface "Mesa" /
  // the raw UUID as fallback labels.
  const { data: tablesData } = useTables();
  const tables = tablesData ?? [];
  const { data: zonesData } = useZones();
  const zones = zonesData ?? [];

  // Search-and-pick state for the Cliente block. Local — the typed
  // query never lands on the draft; only the picked guest does.
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery), 250);
    return () => clearTimeout(id);
  }, [searchQuery]);
  const shouldSearch = debouncedQuery.length >= 2;
  const { data: searchResults } = useGuests(shouldSearch ? debouncedQuery : undefined);
  const visibleResults = shouldSearch ? (searchResults ?? []) : [];

  // Dedup lookup runs in parallel: if what the operator typed looks
  // like a phone, ask the backend whether it already belongs to
  // someone in the CRM. We surface that match prominently in the
  // dropdown (it's almost always the right pick) and gate the
  // "Crear cliente nuevo" affordance behind a confirm so the operator
  // can't accidentally trigger a silent re-link at save time.
  const debouncedPhoneDigits = debouncedQuery.replace(/\D/g, "");
  const phoneForLookup = debouncedPhoneDigits.length >= 7 ? debouncedQuery : "";
  const { data: duplicateGuests } = useGuestDuplicates(phoneForLookup || undefined);
  const inputPhoneNorm = normalizePhoneForCompare(phoneForLookup);
  const duplicateGuest = useMemo(() => {
    if (!phoneForLookup || !duplicateGuests || duplicateGuests.length === 0) return null;
    return (
      duplicateGuests.find((g) => normalizePhoneForCompare(g.phone) === inputPhoneNorm) ?? null
    );
  }, [phoneForLookup, duplicateGuests, inputPhoneNorm]);

  // Confirm bar state for the "Crear cliente nuevo" path when a
  // dedup match exists. The operator must explicitly choose between
  // re-using the existing CRM record or proceeding with a brand-new
  // client.
  const [pendingNewClientName, setPendingNewClientName] = useState<string | null>(null);

  // Second dedup lookup keyed off the editable phone in the Cliente
  // block (after picking / creating). The first lookup above is keyed
  // off the search query; that one stops being relevant once we leave
  // the search. This one catches the "operator typed a brand-new name
  // and then a phone that already belongs to someone" case the modal
  // surfaced with a blue banner — without it the backend silently
  // re-links the reservation to whatever guest owns the phone.
  const draftPhoneDigits = draft.guestPhone.replace(/\D/g, "");
  const phoneForPostPickLookup = draftPhoneDigits.length >= 7 ? draft.guestPhone : "";
  const { data: postPickDuplicates } = useGuestDuplicates(phoneForPostPickLookup || undefined);
  const postPickMatch = useMemo(() => {
    if (!phoneForPostPickLookup || !postPickDuplicates || postPickDuplicates.length === 0)
      return null;
    const norm = normalizePhoneForCompare(phoneForPostPickLookup);
    const match = postPickDuplicates.find((g) => normalizePhoneForCompare(g.phone) === norm);
    // Hide the banner when the match is the guest already picked —
    // editing the phone of a picked guest is a normal flow and the
    // backend resolves the link via ``guestId`` anyway.
    if (!match || match.id === draft.guestId) return null;
    return match;
  }, [phoneForPostPickLookup, postPickDuplicates, draft.guestId]);

  // Programmatic focus on the search input — the linter rejects the
  // declarative `autoFocus` attribute (a11y rule). Same end behaviour:
  // when the search becomes visible (initial open OR after the
  // operator clicked "Cambiar cliente") the input takes focus so the
  // operator can start typing. Moved out of the ref callback so we
  // don't fight foreign focus (e.g. devtools) on every re-render.
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const hasPickedGuest = draft.guestName.trim().length > 0;
  useEffect(() => {
    if (!hasPickedGuest && pendingNewClientName === null) {
      searchInputRef.current?.focus();
    }
  }, [hasPickedGuest, pendingNewClientName]);

  // Local state for the dial-code split. We can't derive ``code`` /
  // ``national`` purely from ``draft.guestPhone`` on every render —
  // when the operator is mid-edit and the code transitions through
  // partial states like ``"+5"`` or ``"+"``, the round-trip through
  // ``draft.guestPhone`` (which only persists when there's a national
  // portion) loses what they're typing. Keep the inputs in local state
  // and reconcile back to ``draft.guestPhone`` only when there's a
  // national number to combine with.
  // Lazy initialisation so ``splitDialCode`` runs once on mount, not
  // on every render — ``useRef(splitDialCode(...))`` would invoke the
  // function each call before discarding the result.
  const [dialCode, setDialCode] = useState(() => splitDialCode(draft.guestPhone).code);
  const [nationalDigits, setNationalDigits] = useState(
    () => splitDialCode(draft.guestPhone).national,
  );
  // Track our last write so we can tell ``selectGuest`` / seed-driven
  // updates of ``draft.guestPhone`` apart from our own writes.
  const lastWrittenPhoneRef = useRef(draft.guestPhone);
  useEffect(() => {
    if (draft.guestPhone === lastWrittenPhoneRef.current) return;
    const { code, national } = splitDialCode(draft.guestPhone);
    setDialCode(code);
    setNationalDigits(national);
    lastWrittenPhoneRef.current = draft.guestPhone;
  }, [draft.guestPhone]);
  const writePhone = (code: string, national: string) => {
    // Bare "+" with national digits would persist as e.g. "+3001234567",
    // which the dedup lookup then queries as a malformed number. Skip
    // the write while the operator is mid-edit on the dial code — the
    // local ``dialCode`` state still shows their progress, the draft
    // stays at its previous valid value, and the next keystroke (which
    // produces a real ``+XX`` prefix) lands cleanly.
    if (code === "+" && national) return;
    const next = national ? `${code}${national}` : "";
    lastWrittenPhoneRef.current = next;
    updateDraft({ guestPhone: next });
  };

  // ── Reserva block plumbing ────────────────────────────────────
  const locationTz = useActiveLocationTimezone();
  const { data: locationSettings } = useLocationSettings();
  const durationMinutes = locationSettings?.default_reservation_duration_minutes ?? 180;
  const isDateDisabled = useCalendarDisabled();
  const [explorerOpen, setExplorerOpen] = useState(false);

  const startsAt = useMemo(
    () =>
      draft.date && draft.time
        ? parseInTz(`${draft.date} ${draft.time}`, "YYYY-MM-DD h:mm A", locationTz).toISOString()
        : "",
    [draft.date, draft.time, locationTz],
  );
  const endsAt = useMemo(
    () => (startsAt ? dayjs(startsAt).add(durationMinutes, "minute").toISOString() : ""),
    [startsAt, durationMinutes],
  );

  // Availability loads once a fecha is set. Pax defaults to 1 for the
  // first fetch so the hora picker can show a generous slot grid
  // before the operator commits to a party size (sequencing is
  // fecha → hora → pax → mesa). Once pax is set we re-fetch to
  // surface only slots that fit the real party; if the picked hora
  // disappears from the new grid the useEffect below clears it.
  const effectivePax = draft.pax ?? 1;
  const { data: availability, isLoading: loadingAvailability } = useAvailability({
    targetDate: draft.date,
    pax: effectivePax,
    features: draft.features,
    enabled: draft.date.length > 0,
  });
  const availabilityIsStale = Boolean(availability && availability.date !== draft.date);
  const availabilityTimes = useMemo(() => {
    const slots = availability?.slots ?? [];
    return slots.map((s) => ({
      time: formatTime(locationTz ? dayjs(s.starts_at).tz(locationTz) : dayjs(s.starts_at)),
      // Keep the raw ISO so cross-midnight snap math anchors to real
      // instants — comparing wall-clock minutes alone makes 23:30 →
      // 00:30 look 1380 min apart instead of 60.
      startsAt: s.starts_at,
      afterHours: Boolean(s.after_hours),
    }));
  }, [availability, locationTz]);

  // Sequencing gates: fecha → hora → pax → mesa. Each celda activates
  // only after its predecessor has a value.
  const dateLocked = draft.date.length > 0;
  const timeLocked = draft.time.length > 0;
  const paxLocked = (draft.pax ?? 0) > 0;
  const canPickTable = dateLocked && timeLocked && paxLocked;

  // Auto-pick the best table once date+time+pax are set. We hit the
  // same ``/explore/`` endpoint TableExplorer uses (instead of the
  // narrower ``/suggest-tables/``) so the recommendation considers
  // registered ``TableGroup`` combinations — not just standalone
  // mesas. Without this, a 4-pax party gets B1 (a 1-cap barra) as the
  // top "suggestion" simply because explore knows about the T1+T2
  // group and suggest-tables doesn't always surface it.
  const explorerQuery = useQuery<ExplorerResult>({
    queryKey: [
      "new-res-explore",
      draft.pax,
      startsAt,
      endsAt,
      draft.zone || null,
      [...draft.features].toSorted().join(","),
    ],
    queryFn: () =>
      apiExplore({
        pax: draft.pax ?? 0,
        starts_at: startsAt,
        ends_at: endsAt,
        preferred_zone_id: draft.zone || null,
        features: draft.features,
        strategy: "strict",
        max_results: 5,
        include_all_singletons: false,
      }),
    enabled: canPickTable && draft.pax !== null && Boolean(startsAt && endsAt),
    staleTime: 30_000,
    // Keep the previous explorer response visible while a refetch is
    // in flight — otherwise pax/hora/feature changes blank out the
    // recommendation for a frame and the operator sees the mesa
    // vanish before the new pick lands.
    placeholderData: (prev) => prev,
  });
  // Pick the recommendation from the explorer's ``free_options``,
  // overriding the backend's "minimum wasted capacity" ranking which
  // otherwise keeps preferring 1-cap barras (B1 for pax=1, B1+B2 for
  // 2, etc.) — the host's intuition is to seat in real mesas first
  // and only fall back to the bar when the room is full. Heuristic:
  //
  //   1. Split groups into "regular" (≥1 mesa with maxCapacity > 1)
  //      and "barra-only" (every mesa is maxCapacity == 1). Try
  //      regulars first; only fall through to barra-only if there
  //      are no regulars (i.e. the rest of the floor is full).
  //   2. Inside the chosen pool, prefer the ones whose ``total_max``
  //      clears ``pax``. If none does, fall back to the group whose
  //      ``total_max`` gets closest to ``pax`` — useful when the
  //      operator's party doesn't have a perfect fit.
  //   3. Among groups with the same ``wasted_max`` (equivalent
  //      goodness of fit), pick one at random. Without this the
  //      operator gets the same mesa every time for the same pax.
  const recommendedTableIds = useMemo(() => {
    const pax = draft.pax ?? 0;
    if (pax <= 0) return [];
    const all = explorerQuery.data?.free_options ?? [];
    if (all.length === 0) return [];
    const isBarraOnly = (s: { table_ids: string[] }) =>
      s.table_ids.every((id) => {
        const t = tables.find((tb) => tb.id === id);
        return t ? t.maxCapacity === 1 : true;
      });
    const regular = all.filter((s) => !isBarraOnly(s));
    const pool = regular.length > 0 ? regular : all;
    const fitting = pool.filter((s) => s.total_max >= pax);
    let candidates: typeof pool;
    if (fitting.length > 0) {
      const minWasted = Math.min(...fitting.map((s) => s.wasted_max));
      candidates = fitting.filter((s) => s.wasted_max === minWasted);
    } else {
      // Nothing in the pool fits — fall back to the closest under-
      // capacity option so the operator at least lands on the
      // largest viable surface.
      const maxCap = Math.max(...pool.map((s) => s.total_max));
      candidates = pool.filter((s) => s.total_max === maxCap);
    }
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    return pick?.table_ids ?? [];
    // ``random`` is only re-rolled when ``free_options`` ref changes
    // (i.e. a new fetch), so the pick stays stable across re-renders
    // and the operator doesn't see the mesa flicker.
  }, [explorerQuery.data, draft.pax, tables]);
  // Snapshot of the recommendation that the auto-apply effect actually
  // wrote to the draft. Diffing the live ``draft.tableIds`` against
  // *this* snapshot (not the live ``recommendedTableIds``) is what
  // tells us "the operator picked something else" — without the
  // snapshot, a backend re-rank or refetch would mutate
  // ``recommendedTableIds`` under us and silently drop the badge even
  // though the operator never touched the picker.
  const lastAppliedRecommendation = useRef<string[] | null>(null);
  // Has the operator deliberately cleared their pick? Used to suppress
  // auto-fill so we don't fight a deliberate "I want to repick"
  // gesture. Distinguishes this from a clear-and-refill caused by
  // changing the date (which we want to re-auto-fill).
  const userClearedPick = useRef(false);
  // Bail-out hook for the date-change handler: it clears tableIds for
  // a fresh recommendation against the new date, so we shouldn't treat
  // that as "operator cleared".
  const resetAutoSuggestGate = useCallback(() => {
    userClearedPick.current = false;
    lastAppliedRecommendation.current = null;
  }, []);
  useEffect(() => {
    if (!canPickTable) return;
    if (userClearedPick.current) return; // deliberate clear, respect it
    const currentIsLastApplied =
      lastAppliedRecommendation.current !== null &&
      draft.tableIds.join(",") === lastAppliedRecommendation.current.join(",");
    // Clear-the-stale path: a prior auto-pick lives in the draft but
    // the latest explorer response carries no recommendation that
    // fits (e.g. pax bumped past the largest available group). Drop
    // the stale mesa + zone so the operator isn't left with an
    // outdated "Recomendada" pick from a different pax.
    if (recommendedTableIds.length === 0) {
      if (currentIsLastApplied) {
        updateDraft({ tableIds: [], zone: "" });
        lastAppliedRecommendation.current = null;
        initialDraftRef.current = useAppStore.getState().newReservationDraft;
      }
      return;
    }
    // Re-apply path: the operator hasn't touched the mesa (current
    // pick still matches the last auto-applied recommendation), but
    // pax / hora / features changed and the backend now ranks a
    // different mesa first. Replace the pick so we don't leave the
    // operator with e.g. a 1-pax barra after they bumped pax to 4.
    const recommendationDiffers =
      recommendedTableIds.join(",") !== lastAppliedRecommendation.current?.join(",");
    if (draft.tableIds.length > 0 && !(currentIsLastApplied && recommendationDiffers)) {
      return; // operator-chosen pick — leave it alone
    }
    // Hold off until the local ``tables`` cache has the suggested id,
    // so the zone derivation actually resolves. Without this, a race
    // between the explorer (returns first) and ``useTables``
    // (still loading) lands a tableId with ``zone:""``, then the
    // length-guard above traps us — the Zona cell stays "—" with the
    // Recomendada badge.
    const recommendedTable = tables.find((t) => t.id === recommendedTableIds[0]);
    if (!recommendedTable) return;
    updateDraft({ tableIds: recommendedTableIds, zone: recommendedTable.zoneId });
    lastAppliedRecommendation.current = recommendedTableIds;
    // Re-baseline ``isDirty`` against the post-auto-pick draft so
    // Volver on an otherwise-pristine surface doesn't pop the discard
    // confirm just because we filled in the mesa for the operator.
    initialDraftRef.current = useAppStore.getState().newReservationDraft;
  }, [canPickTable, draft.tableIds, recommendedTableIds, tables, updateDraft]);
  // Detect deliberate user clears (Cambiar mesa wiping tableIds with
  // date+pax+time still set). The date-change handler resets the gate
  // separately, so a date-driven clear is not confused with a manual
  // one.
  useEffect(() => {
    if (draft.tableIds.length === 0 && canPickTable && lastAppliedRecommendation.current) {
      userClearedPick.current = true;
      lastAppliedRecommendation.current = null;
    }
  }, [draft.tableIds.length, canPickTable]);
  // ``pickIsRecommended`` is now a pure derivation: the pick matches
  // the snapshot we wrote at auto-apply time.
  const pickIsRecommended =
    lastAppliedRecommendation.current !== null &&
    draft.tableIds.join(",") === lastAppliedRecommendation.current.join(",");
  // If the operator picks something other than the recommendation
  // (after the auto-apply, via TableExplorer), drop the snapshot so
  // the badge stops claiming "Recomendada". Diverging the pick also
  // counts as a deliberate change, so subsequent auto-apply attempts
  // bail until a date-driven gate reset.
  useEffect(() => {
    if (
      lastAppliedRecommendation.current &&
      draft.tableIds.length > 0 &&
      draft.tableIds.join(",") !== lastAppliedRecommendation.current.join(",")
    ) {
      lastAppliedRecommendation.current = null;
      userClearedPick.current = true;
    }
  }, [draft.tableIds]);

  // Recalc-on-pax-change: when pax bumps after picking hora, the new
  // availability grid may drop the previously picked slot (e.g. that
  // time can no longer accommodate the bigger party). If the picked
  // time no longer matches a grid slot, snap to the closest one — that
  // covers both the pax-bump case AND the time-travel seed, where
  // ``previewTime`` is a wall-clock instant (e.g. "8:23 PM") that
  // rarely lands on a discrete slot. Same pattern the detail-edit
  // uses when fecha changes.
  useEffect(() => {
    if (!draft.time || availabilityTimes.length === 0) return;
    if (availabilityIsStale) return; // grid is mid-refetch, ignore
    const stillAvailable = availabilityTimes.some((t) => t.time === draft.time);
    if (stillAvailable) return;
    // Anchor the draft hora to draft.date in the location's timezone
    // and compare against the slot's actual ISO. This makes
    // cross-midnight slots (service window 7 PM → 1 AM next day) snap
    // correctly: 23:30 → 00:30 reads as 60 minutes apart, not 1380.
    const draftInstant = parseInTz(`${draft.date} ${draft.time}`, "YYYY-MM-DD h:mm A", locationTz);
    if (!draftInstant.isValid()) {
      updateDraft({ time: "" });
      return;
    }
    const draftMs = draftInstant.valueOf();
    const closest = availabilityTimes.reduce<{ time: string; delta: number } | null>(
      (best, slot) => {
        const delta = Math.abs(dayjs(slot.startsAt).valueOf() - draftMs);
        if (!best || delta < best.delta) return { time: slot.time, delta };
        return best;
      },
      null,
    );
    updateDraft({ time: closest?.time ?? "" });
  }, [availabilityTimes, availabilityIsStale, draft.time, draft.date, locationTz, updateDraft]);

  // Dry-run validation — surfaces the same banner the detail-edit
  // shows. Catches: capacity (mesa won't hold pax), features (toggled
  // mascota / silla on a mesa that doesn't support them), zone, etc.
  // Fires only once date+time+pax+mesa are all set; below that the
  // form has nothing to validate yet.
  const dryRunQuery = useQuery({
    queryKey: [
      "new-res-dry-run",
      draft.date,
      draft.time,
      draft.pax,
      draft.zone,
      draft.tableIds.join(","),
      [...draft.features].toSorted().join(","),
    ],
    queryFn: () =>
      apiDryRun({
        pax: draft.pax ?? 0,
        starts_at: startsAt,
        ends_at: endsAt,
        preferred_zone_id: draft.zone || null,
        table_ids: draft.tableIds,
        features: draft.features,
      }),
    enabled: canPickTable && draft.tableIds.length > 0,
    staleTime: 5_000,
  });

  // ``dryRunQuery`` feeds the inline banner only — it intentionally
  // does NOT block the submit. The operator is allowed to pick a
  // mesa that the dry-run flags (e.g. a mesa without ``pet_friendly``
  // when the guest brings a dog). On submit the backend rejects with
  // a structured 409 and ``TableConstraintsOverrideDialog`` opens
  // ("Atención antes de guardar"), letting the operator confirm and
  // proceed or back out without losing the draft. Mirrors how the
  // detail-edit flow works.

  // Phone + email pre-save validation — same shape the detail-edit
  // uses. Empty is OK (both fields are optional); non-empty values
  // must be plausible so we don't bounce a 422 from the backend.
  // Run the typed phone through libphonenumber so the staff create flow
  // matches the contract the backend's ``django-phonenumber-field``
  // enforces. A plain ``7..15 digits`` gate let "1234567" through and
  // the operator hit a 422 only at save time.
  const phoneIsValid = isValidPhone(draft.guestPhone);
  const emailIsValid = (() => {
    const trimmed = draft.guestEmail.trim();
    if (trimmed.length === 0) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  })();
  const contactIsValid = phoneIsValid && emailIsValid;

  // ── Submit + discard gates ────────────────────────────────────
  // "Dirty" = the operator typed something *past the seed*. The store
  // pre-fills ``date`` (today / browsing day) and the time-travel entry
  // also seeds ``time`` and ``tableIds``, so comparing against
  // empty-string baselines fires the discard-confirm on every Volver,
  // even on a pristine surface. Snapshot the seed at mount and diff
  // against that instead.
  const initialDraftRef = useRef(draft);
  const isDirty = useMemo(() => {
    const seed = initialDraftRef.current;
    return (
      draft.guestId !== seed.guestId ||
      draft.guestName !== seed.guestName ||
      draft.guestPhone !== seed.guestPhone ||
      draft.guestEmail !== seed.guestEmail ||
      draft.pax !== seed.pax ||
      draft.date !== seed.date ||
      draft.time !== seed.time ||
      draft.zone !== seed.zone ||
      draft.tableIds.join(",") !== seed.tableIds.join(",") ||
      [...draft.features].toSorted().join(",") !== [...seed.features].toSorted().join(",") ||
      draft.isVip !== seed.isVip ||
      draft.notes !== seed.notes
    );
  }, [draft]);

  const canSubmitBase = draft.guestName.trim().length > 0 && dateLocked && timeLocked && paxLocked;
  const canSubmit = canSubmitBase && draft.tableIds.length > 0;
  // Best-effort: a party that fits no table can still be booked without one.
  const canSubmitTableless = canSubmitBase && draft.tableIds.length === 0;

  // ``discardConfirm`` carries the intent the confirm bar acts on:
  //   - ``"exit"`` — close the surface when confirmed (Volver / Cancelar).
  //   - ``"reset"`` — keep the surface open but clear the draft when
  //     confirmed (operator clicked ``+`` again).
  const [discardConfirm, setDiscardConfirm] = useState<"exit" | "reset" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ``setIsSubmitting`` is async — two rapid clicks can both see
  // ``isSubmitting === false`` in their closure and fire
  // ``createReservation`` twice. The ref flips synchronously inside
  // ``submit`` so the second click bails before reaching the API call.
  // The state is kept around for the button's "Guardando…" label.
  const submittingRef = useRef(false);

  const attemptClose = () => {
    if (isDirty) {
      setDiscardConfirm("exit");
    } else {
      onClose();
    }
  };

  // Reset-tick watcher — fires every time an entry point (the ``+``
  // button, the dashboard CTA, the map's time-travel free-mesa click,
  // etc.) asks for a fresh draft while the surface is already open.
  // Behaviour depends on whether the draft is dirty and whether the
  // entry point parked a seed alongside:
  //
  //   - dirty                 → pop discard confirm; the seed (if any)
  //                              waits in the store and is consumed
  //                              when the operator confirms Sí descartar.
  //   - pristine + no seed   → no-op. Nothing to clear, nothing to add.
  //   - pristine + seed      → silently apply the seed onto the draft
  //                              so the operator gets the new context
  //                              without an unnecessary confirm.
  const lastSeenTickRef = useRef(resetTick);
  useEffect(() => {
    if (resetTick === lastSeenTickRef.current) return;
    lastSeenTickRef.current = resetTick;
    if (isDirty) {
      setDiscardConfirm("reset");
      return;
    }
    if (pendingSeed) {
      updateDraft(pendingSeed);
      // Re-baseline so the just-seeded draft doesn't count as dirty.
      initialDraftRef.current = useAppStore.getState().newReservationDraft;
      clearPendingSeed();
    }
    // pristine + no seed → fall through, no-op.
  }, [resetTick, isDirty, pendingSeed, updateDraft, clearPendingSeed]);

  const submit = async () => {
    if ((!canSubmit && !canSubmitTableless) || draft.pax === null || submittingRef.current) return;
    if (draft.tableIds.length > 0) {
      const { tables: allTables, zones: allZones } = useAppStore.getState();
      if (!(await confirmReservationTables(draft.tableIds, allTables, allZones))) return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    // Read at submit time: set when "Reservar" opened this surface from a
    // cotización, ``null`` for a plain new reservation. Threaded into the
    // payload so the backend excludes the quote's hold + converts it.
    const convertingQuoteId = useAppStore.getState().convertingQuoteId;
    createReservation(
      {
        guestId: draft.guestId ?? undefined,
        guestName: draft.guestName.trim(),
        guestPhone: draft.guestPhone.trim() || undefined,
        guestEmail: draft.guestEmail.trim() || undefined,
        pax: draft.pax,
        date: draft.date,
        time: draft.time,
        // Forward the location's configured default duration. Without
        // this the store falls back to a hardcoded 180 min (3 h) which
        // over-stretches the booking past the operator's "Límite por
        // mesa" setting — e.g. a 1pm booking on a venue with 2:30h
        // default ends at 4pm instead of 3:30pm, colliding with the
        // next reservation and surfacing as a misleading "ya ocupada".
        durationMinutes,
        tableIds: draft.tableIds.length > 0 ? draft.tableIds : undefined,
        preferredZone: draft.zone || undefined,
        features: draft.features.length > 0 ? draft.features : undefined,
        isVip: draft.isVip,
        notes: draft.notes.trim() || undefined,
        // Ship the redistribute chain staged by the explorer pick — the
        // backend's create endpoint applies them atomically in the same
        // transaction as the new reservation.
        moves: draft.moves.length > 0 ? draft.moves : undefined,
        origin: "seimseim",
        quoteRequestId: convertingQuoteId ?? undefined,
      },
      {
        // Only dismiss the surface when the backend has confirmed —
        // including after the operator OKed an override dialog. On a
        // cancelled override or hard failure we re-enable the button
        // so the draft survives and the operator can adjust without
        // re-typing everything.
        onApiSuccess: () => {
          submittingRef.current = false;
          setIsSubmitting(false);
          // The backend already converted the quote — refresh the
          // cotizaciones list so its Estado flips to "Reservada".
          if (convertingQuoteId) {
            queryClient.invalidateQueries({ queryKey: ["cotizaciones"] });
          }
          onClose();
        },
        onApiCancel: () => {
          submittingRef.current = false;
          setIsSubmitting(false);
        },
      },
    );
  };

  const selectGuest = (g: Guest) => {
    updateDraft({
      guestId: g.id,
      guestName: g.name,
      guestPhone: g.phone || "",
      guestEmail: g.email || "",
    });
    setSearchQuery("");
    setDebouncedQuery("");
  };

  const changeClient = () => {
    // Resets the picked guest so the search input renders again.
    // Features (pet, accessible) survive — they describe the booking,
    // not the guest, and re-typing them on every retry would be friction.
    updateDraft({
      guestId: null,
      guestName: "",
      guestPhone: "",
      guestEmail: "",
    });
  };

  const toggleFeature = (feat: string) => {
    const current = draft.features;
    const next = current.includes(feat) ? current.filter((f) => f !== feat) : [...current, feat];
    updateDraft({ features: next });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header — Volver + "NUEVA RESERVA" pill, matching DetailView.
          Volver goes through ``attemptClose`` so a dirty draft can't
          be thrown away by an accidental click. When the operator
          tried to bail with edits, this same header is replaced by
          the red discard-confirm bar (same slot, same height) so the
          confirm lives at the top like other panel-level actions
          (cancelar reserva, marcar no-show, etc.) instead of inside
          the footer where the eye naturally heads to "Crear". */}
      {discardConfirm ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-red-200 bg-red-50 px-5 py-3">
          <span className="text-[14px] font-semibold text-red-700">¿Descartar esta reserva?</span>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => {
                // Cancelling a reset request drops the parked seed
                // alongside, since the operator explicitly chose to
                // keep their current draft.
                if (discardConfirm === "reset") clearPendingSeed();
                setDiscardConfirm(null);
              }}
              className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-[13px] font-medium text-red-800 hover:bg-red-100"
            >
              Continuar
            </button>
            <button
              type="button"
              onClick={() => {
                if (discardConfirm === "reset") {
                  resetDraft();
                  // If the reset request came with a seed (e.g. the
                  // time-travel free-mesa click parked time +
                  // tableIds in the store while the surface had a
                  // dirty draft), merge it onto the now-blank draft
                  // so the operator lands on the new context they
                  // asked for.
                  if (pendingSeed) {
                    updateDraft(pendingSeed);
                  }
                  clearPendingSeed();
                  // The store reset clears ``draft.*`` but local
                  // component state (the search box, the dial-code
                  // split, the "Crear cliente nuevo" intent, the
                  // table-suggestion guards) survives a re-render
                  // and would surface stale "JJJ"-style values.
                  // Wipe everything that mirrors the draft so the
                  // surface looks like a fresh open.
                  setSearchQuery("");
                  setDebouncedQuery("");
                  setPendingNewClientName(null);
                  setDialCode("+57");
                  setNationalDigits("");
                  resetAutoSuggestGate();
                  // Re-baseline ``isDirty`` against the post-reset
                  // draft so a subsequent Volver on the untouched
                  // surface doesn't pop the confirm again. Read the
                  // post-reset value from the store directly — the
                  // local ``draft`` is still the pre-reset snapshot
                  // until the next render.
                  initialDraftRef.current = useAppStore.getState().newReservationDraft;
                  setDiscardConfirm(null);
                } else {
                  onClose();
                }
              }}
              className="rounded-md bg-red-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-red-700"
            >
              Sí, descartar
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 border-b border-border bg-surface px-5 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={attemptClose}
            className="-ml-2 inline-flex h-8 items-center gap-1 px-2 text-text-secondary hover:text-text-primary"
          >
            <ChevronLeft className="size-4" />
            Volver
          </Button>
          <span className="ml-auto text-[12px] font-semibold text-text-primary">Nueva reserva</span>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
        {/* Cliente block — empty state shows a search; picking a result
            (or letting the operator type a fresh name) transforms it
            into the display form below. Same visual language as
            DetailView's Cliente block so the operator learns one
            paradigm. */}
        <div className="flex flex-col gap-2">
          <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-text-secondary">
            Cliente
          </div>

          {!hasPickedGuest ? (
            pendingNewClientName !== null && duplicateGuest ? (
              // Option C — explicit confirm when the operator chose
              // "Crear cliente nuevo" but the typed phone already
              // belongs to someone. Forces an active decision so the
              // backend's silent re-link doesn't surprise anyone.
              <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="text-[13px] font-semibold text-amber-900">
                  Ese teléfono ya pertenece a {duplicateGuest.name}
                </p>
                <p className="text-[12px] text-amber-800">
                  Si creás cliente nuevo, la reserva se va a vincular igual al cliente existente
                  cuando el sistema haga el match por teléfono.
                </p>
                <div className="mt-1 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      selectGuest(duplicateGuest);
                      setPendingNewClientName(null);
                    }}
                    className="rounded-md bg-text-primary px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-text-primary/90"
                  >
                    Usar a {duplicateGuest.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Same split as the inline path — a phone typed
                      // into the search lands in ``guestPhone``, not the
                      // name field.
                      const typed = pendingNewClientName.trim();
                      if (looksLikePhone(typed)) {
                        updateDraft({
                          guestId: null,
                          guestName: "",
                          guestPhone: typed,
                          guestEmail: "",
                        });
                      } else {
                        updateDraft({
                          guestId: null,
                          guestName: typed,
                          guestPhone: "",
                          guestEmail: "",
                        });
                      }
                      setPendingNewClientName(null);
                    }}
                    className="rounded-md border border-amber-400 bg-white px-3 py-1.5 text-[13px] font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Sí, cliente nuevo
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Nombre o teléfono…"
                  className="w-full rounded-lg border border-[#c8c8c2] bg-white px-3 py-2.5 text-[14px] text-text-primary outline-none focus:border-text-primary"
                  aria-label="Buscar cliente"
                />
                {/* Option B — dedup match surfaces at the top of the
                    dropdown with a distinct badge, so the operator
                    sees the existing CRM record before they decide to
                    create a new one. */}
                {(duplicateGuest || visibleResults.length > 0) && (
                  <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-60 overflow-y-auto rounded-lg border border-[#c8c8c2] bg-white shadow-lg">
                    {duplicateGuest && (
                      <button
                        type="button"
                        onClick={() => selectGuest(duplicateGuest)}
                        className="flex w-full items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-left text-[13px] hover:bg-amber-100"
                      >
                        <span className="flex flex-col gap-0.5">
                          <span className="font-medium text-amber-900">{duplicateGuest.name}</span>
                          <span className="text-[10.5px] uppercase font-bold tracking-wider text-amber-700">
                            Mismo teléfono
                          </span>
                        </span>
                        <span className="text-[11.5px] text-amber-800">
                          {duplicateGuest.phone || duplicateGuest.email}
                        </span>
                      </button>
                    )}
                    {visibleResults
                      .filter((g) => g.id !== duplicateGuest?.id)
                      .map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => selectGuest(g)}
                          className="flex w-full items-center justify-between gap-2 border-b border-[#ececea] px-3 py-2 text-left text-[13px] last:border-b-0 hover:bg-[#f7f6f3]"
                        >
                          <span className="font-medium text-text-primary">{g.name}</span>
                          <span className="text-[11.5px] text-text-secondary">
                            {g.phone || g.email}
                          </span>
                        </button>
                      ))}
                  </div>
                )}
                {/* "Crear cliente nuevo" affordance — when a dedup
                    match exists, intercept and show the confirm bar
                    (Option C) instead of silently committing. */}
                {shouldSearch && searchQuery.trim().length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const typed = searchQuery.trim();
                      if (duplicateGuest) {
                        setPendingNewClientName(typed);
                        return;
                      }
                      // If the operator typed a phone (placeholder reads
                      // "Nombre o teléfono…"), route it to ``guestPhone``
                      // — otherwise it lands as the name and the backend
                      // re-links the reservation to whatever guest owns
                      // that phone, leaving "the phone" displayed in the
                      // name column.
                      if (looksLikePhone(typed)) {
                        updateDraft({
                          guestId: null,
                          guestName: "",
                          guestPhone: typed,
                          guestEmail: "",
                        });
                      } else {
                        updateDraft({
                          guestId: null,
                          guestName: typed,
                          guestPhone: "",
                          guestEmail: "",
                        });
                      }
                    }}
                    className={
                      "rounded-lg border border-dashed border-text-secondary/60 bg-white px-3 py-2 text-left text-[13px] text-text-primary shadow-sm hover:bg-[#f7f6f3] " +
                      (visibleResults.length > 0 || duplicateGuest
                        ? "mt-2 block w-full"
                        : "absolute left-0 right-0 top-full z-30 mt-1")
                    }
                  >
                    <span className="font-medium">Crear cliente nuevo</span>
                    <span className="ml-2 text-[11.5px] text-text-secondary">
                      "{searchQuery.trim()}"
                    </span>
                  </button>
                )}
              </div>
            )
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-[18px] font-extrabold tracking-tight leading-tight">
                {draft.guestId === null ? (
                  // Cliente nuevo — keep the name editable in place so a
                  // typo (or a phone landing in the name field by
                  // accident) can be corrected without dropping back to
                  // the search.
                  <input
                    type="text"
                    value={draft.guestName}
                    onChange={(e) => updateDraft({ guestName: e.target.value })}
                    placeholder="Nombre del cliente"
                    aria-label="Nombre del cliente"
                    className="min-w-0 flex-1 border-b border-dashed border-text-secondary/60 bg-transparent pb-0.5 text-[18px] font-extrabold tracking-tight leading-tight outline-none focus:border-text-primary"
                  />
                ) : (
                  <span>{draft.guestName}</span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <FeatureToggleChip
                    selected={draft.features.includes("pet_friendly")}
                    onClick={() => toggleFeature("pet_friendly")}
                    label="Mascota"
                    icon={<Dog className="size-3" />}
                  />
                  <FeatureToggleChip
                    selected={draft.features.includes("accessible")}
                    onClick={() => toggleFeature("accessible")}
                    label="Accesibilidad"
                    icon={<Accessibility className="size-3" />}
                  />
                  <FeatureToggleChip
                    selected={draft.isVip}
                    onClick={() => updateDraft({ isVip: !draft.isVip })}
                    label="VIP"
                    icon={
                      <Star
                        className={"size-3 " + (draft.isVip ? "text-orange-500" : "")}
                        fill={draft.isVip ? "currentColor" : "none"}
                      />
                    }
                  />
                </span>
              </div>

              <div className="rounded-lg border border-[#c8c8c2] bg-white overflow-hidden">
                <div className="grid grid-cols-[22px_1fr_auto] items-center gap-1.5 border-b border-[#ececea] px-2.5 py-1.5 text-[13px] last:border-b-0">
                  <span className="text-center font-mono text-[12px] text-text-secondary">☎</span>
                  {/* Dial code + national digits split. The combined
                      ``draft.guestPhone`` stays in E.164 (e.g.
                      ``+573001234567``) so the backend's
                      ``PhoneNumberField`` accepts it and links like
                      ``wa.me/<phone>`` keep working. */}
                  {/* Editable dial-code box + national digits. The
                      operator can type any ``+XX`` and the ISO label
                      next to it resolves live via ``isoForDialCode``
                      (libphonenumber-backed). Combined value stays in
                      E.164 so backend storage + ``wa.me/...`` links
                      keep working. */}
                  <div className="flex items-center gap-1">
                    <div className="flex items-stretch rounded border border-dashed border-text-secondary/60 bg-white focus-within:border-solid focus-within:border-text-primary">
                      <span className="flex items-center border-r border-dashed border-text-secondary/40 px-1 font-mono text-[10px] uppercase tracking-wider text-text-secondary">
                        {isoForDialCode(dialCode) || "—"}
                      </span>
                      {/* Static ``+`` prefix + a digit-only input. Keeping
                          the ``+`` outside the controlled value avoids the
                          caret-jumps-to-the-left bug some browsers exhibit
                          when the leading character is non-editable but
                          part of the value. */}
                      <span className="flex items-center pl-1.5 font-mono text-[12px] text-text-primary">
                        +
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={dialCode.startsWith("+") ? dialCode.slice(1) : dialCode}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                          const next = digits ? `+${digits}` : "+";
                          setDialCode(next);
                          writePhone(next, nationalDigits);
                        }}
                        aria-label="Indicativo del país"
                        className="w-10 bg-transparent pr-1.5 py-0.5 font-mono text-[12px] text-text-primary outline-none"
                      />
                    </div>
                    <input
                      type="tel"
                      inputMode="tel"
                      value={nationalDigits}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        setNationalDigits(digits);
                        writePhone(dialCode, digits);
                      }}
                      placeholder="300 000 0000"
                      aria-invalid={!phoneIsValid}
                      aria-label="Número de teléfono"
                      className={
                        "min-w-0 flex-1 rounded border bg-white px-1.5 py-0.5 font-mono text-[12px] text-text-primary outline-none focus:border-solid " +
                        (phoneIsValid
                          ? "border-dashed border-text-secondary/60 focus:border-text-primary"
                          : "border-solid border-red-500 focus:border-red-600")
                      }
                    />
                  </div>
                  <PenLine className="size-3 text-text-secondary/70" />
                </div>
                <div className="grid grid-cols-[22px_1fr_auto] items-center gap-1.5 px-2.5 py-1.5 text-[13px]">
                  <span className="text-center font-mono text-[12px] text-text-secondary">✉</span>
                  <input
                    type="email"
                    inputMode="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    value={draft.guestEmail}
                    onChange={(e) => updateDraft({ guestEmail: e.target.value })}
                    placeholder="nombre@correo.com"
                    aria-invalid={!emailIsValid}
                    className={
                      "rounded border bg-white px-1.5 py-0.5 text-[12.5px] text-text-primary outline-none focus:border-solid " +
                      (emailIsValid
                        ? "border-dashed border-text-secondary/60 focus:border-text-primary"
                        : "border-solid border-red-500 focus:border-red-600")
                    }
                    aria-label="Editar email"
                  />
                  <PenLine className="size-3 text-text-secondary/70" />
                </div>
              </div>

              {/* Phone-based dedup banner — surfaces when the operator
                  edits the phone after picking / creating and the new
                  number already belongs to a different CRM record.
                  Mirrors the blue banner the deleted modal showed.
                  The backend would otherwise silently re-link the
                  reservation by phone, so we offer an explicit
                  "Usar a X" jump. */}
              {postPickMatch && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-blue-300/60 bg-blue-50 px-2.5 py-1.5 text-[12px] text-blue-900">
                  <span>
                    Ese teléfono pertenece a{" "}
                    <strong className="font-semibold">{postPickMatch.name}</strong>. La reserva se
                    va a vincular a ese cliente.
                  </span>
                  <button
                    type="button"
                    onClick={() => selectGuest(postPickMatch)}
                    className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                  >
                    Usar ese cliente
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={changeClient}
                className="self-start text-[12px] font-semibold text-text-primary underline underline-offset-4 hover:text-text-secondary"
              >
                Cambiar cliente
              </button>
            </>
          )}
        </div>

        {/* Reserva block — same visual language as DetailView. Sequenced:
            until the operator picks a fecha the rest are dashed
            placeholders ("rayitas") and the table picker stays
            unavailable. Changing fecha clears hora; changing pax
            re-runs availability. */}
        <div className="flex flex-col gap-1.5">
          <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-text-secondary">
            Reserva
          </div>
          <div className="rounded-[10px] border border-[#c8c8c2] bg-[#f7f6f3] overflow-hidden">
            <div className="p-3.5">
              {/* Date strip — the most important field of the
                  Reserva block, so it gets the largest type. Label
                  prefix ("Hoy" / "Mañana" / "Jueves") makes the
                  context obvious without forcing the operator to do
                  the mental math. */}
              <div className="mb-2.5 flex items-center gap-2">
                <Popover>
                  <PopoverTrigger className="inline-flex items-center gap-2 rounded-md border border-dashed border-text-secondary/60 bg-white px-3 py-1.5 text-[14px] font-semibold text-text-primary outline-none hover:bg-[#f7f6f3] focus:border-solid focus:border-text-primary">
                    {dateLocked ? formatBookingDateLabel(draft.date, locationTz) : "Elige fecha"}
                    <CalendarIcon className="size-4 text-text-secondary/70" />
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={draft.date ? dayjs(draft.date).toDate() : undefined}
                      onSelect={(d) => {
                        if (!d) return;
                        const next = dayjs(d).format("YYYY-MM-DD");
                        if (next !== draft.date) {
                          // Mirror the detail-edit behaviour: changing
                          // the date clears the time + mesa/zona so the
                          // operator re-picks against the new day's
                          // availability. Reset the auto-suggest gate
                          // so the recommendation re-fires for the new
                          // date instead of staying empty.
                          resetAutoSuggestGate();
                          updateDraft({
                            date: next,
                            time: "",
                            tableIds: [],
                            zone: "",
                          });
                        }
                      }}
                      disabled={isDateDisabled}
                    />
                  </PopoverContent>
                </Popover>
                {!dateLocked && <PenLine className="size-3 text-text-secondary/70" />}
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                {/* Hora cell — sources slots from the same useAvailability
                    hook the detail-edit uses, with stale detection so a
                    pax change while we wait doesn't surface a wrong slot
                    grid. */}
                <div
                  className={
                    "rounded-lg border bg-white px-3 py-2 " +
                    (dateLocked ? "border-dashed border-text-secondary/60" : "border-[#c8c8c2]")
                  }
                >
                  <div className="mb-0.5 flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                    Hora
                    {dateLocked && <PenLine className="size-2.5" />}
                  </div>
                  {!dateLocked ? (
                    <span className="font-mono text-[22px] font-extrabold leading-none text-text-secondary/40">
                      —
                    </span>
                  ) : (loadingAvailability && availabilityTimes.length === 0) ||
                    availabilityIsStale ? (
                    <span className="text-[12px] text-text-secondary">Cargando…</span>
                  ) : availabilityTimes.length === 0 ? (
                    <span className="text-[12px] text-text-secondary">Sin horario</span>
                  ) : (
                    <select
                      value={draft.time}
                      onChange={(e) => updateDraft({ time: e.target.value })}
                      aria-invalid={draft.time === ""}
                      className={
                        "w-full bg-transparent font-mono text-[18px] font-extrabold leading-none outline-none " +
                        (draft.time === "" ? "text-red-600" : "")
                      }
                    >
                      {draft.time === "" && (
                        <option value="" disabled>
                          Elige hora
                        </option>
                      )}
                      {availabilityTimes.map((t) => (
                        <option key={t.time} value={t.time}>
                          {t.time}
                          {t.afterHours ? " · Solo staff" : ""}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Personas cell — stepper from 1 up. Activates once
                    Hora is locked (sequence: fecha → hora → pax →
                    mesa). Changing pax re-runs availability; if the
                    picked hora drops out of the new grid the
                    useEffect above clears it. */}
                <div
                  className={
                    "rounded-lg border bg-white px-3 py-2 " +
                    (timeLocked ? "border-dashed border-text-secondary/60" : "border-[#c8c8c2]")
                  }
                >
                  <div className="mb-0.5 flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                    Personas
                    {timeLocked && <PenLine className="size-2.5" />}
                  </div>
                  {!timeLocked ? (
                    <span className="font-mono text-[22px] font-extrabold leading-none text-text-secondary/40">
                      —
                    </span>
                  ) : (
                    <div className="flex items-center justify-between gap-1">
                      <button
                        type="button"
                        onClick={() => updateDraft({ pax: Math.max(1, (draft.pax ?? 1) - 1) })}
                        disabled={!paxLocked}
                        className="flex h-7 w-7 items-center justify-center rounded border border-[#c8c8c2] bg-white text-[16px] font-bold text-text-primary hover:bg-[#f7f6f3] disabled:opacity-40"
                        aria-label="Quitar una persona"
                      >
                        −
                      </button>
                      <span className="font-mono text-[22px] font-extrabold leading-none text-text-primary">
                        {draft.pax ?? "—"}
                        {paxLocked && (
                          <span className="ml-0.5 text-[11px] font-medium tracking-wider text-text-secondary">
                            pax
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateDraft({ pax: (draft.pax ?? 0) + 1 })}
                        className="flex h-7 w-7 items-center justify-center rounded border border-[#c8c8c2] bg-white text-[16px] font-bold text-text-primary hover:bg-[#f7f6f3]"
                        aria-label="Agregar una persona"
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* MESA + ZONA — Mesa shows "elegir en plano" placeholder
                  until fecha + hora + pax are all set. Then it becomes
                  a clickable button that opens the explorer drawer (the
                  canvas-plan picker comes in Phase 2). Zona is display
                  only, auto-derived from the picked mesa. */}
              <div className="mt-2.5 grid grid-cols-[1fr_auto] items-stretch gap-2">
                <button
                  type="button"
                  onClick={() => canPickTable && setExplorerOpen(true)}
                  disabled={!canPickTable}
                  className={
                    "flex flex-col gap-1.5 rounded-lg border bg-white px-3 py-2 text-left " +
                    (canPickTable
                      ? "border-dashed border-text-secondary/60 hover:bg-[#f7f6f3]"
                      : "border-[#c8c8c2] cursor-not-allowed")
                  }
                >
                  <span className="inline-flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                    {draft.tableIds.length > 1 ? "Mesas" : "Mesa"}
                    {canPickTable && <PenLine className="size-2.5" />}
                    {pickIsRecommended && draft.tableIds.length > 0 && (
                      <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wider text-emerald-800">
                        Recomendada
                      </span>
                    )}
                  </span>
                  {draft.tableIds.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {draft.tableIds
                        .map((id) => ({
                          id,
                          name: tables.find((tb) => tb.id === id)?.name || "Mesa",
                        }))
                        .toSorted((a, b) => compareTableNames(a.name, b.name))
                        .map(({ id, name }) => (
                          <span
                            key={id}
                            className="rounded bg-black px-2 py-0.5 text-[13px] font-extrabold leading-none text-white"
                          >
                            {name}
                          </span>
                        ))}
                    </div>
                  ) : canPickTable ? (
                    <span className="text-[12px] text-text-secondary">elegir en plano</span>
                  ) : (
                    <span className="font-mono text-[22px] font-extrabold leading-none text-text-secondary/40">
                      —
                    </span>
                  )}
                </button>

                <div className="flex min-w-[110px] flex-col gap-0.5 rounded-lg border border-[#c8c8c2] bg-white px-3 py-2">
                  <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-text-secondary">
                    Zona
                  </span>
                  {draft.zone ? (
                    <span className="text-[14px] font-bold leading-tight">
                      {zones.find((z) => z.id === draft.zone)?.name ?? draft.zone}
                    </span>
                  ) : (
                    <span className="font-mono text-[22px] font-extrabold leading-none text-text-secondary/40">
                      —
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dry-run banner — flags features/capacity/zone mismatches
            against the picked mesa BEFORE the operator submits, same
            UX as the detail-edit dry-run. Without this, toggling
            "Mascota" after a mesa pick used to silently sail through
            until backend rejected on save. */}
        {canPickTable && draft.tableIds.length > 0 && (
          <EditDryRunBanner
            query={dryRunQuery}
            hasTables
            hasPendingMoves={false}
            // The waste-hint flow (oversized mesa for pax) is a
            // detail-edit affordance — for Nueva reserva it lives
            // out of scope, so we pass empty defaults that disable
            // the inline subset suggestion UI.
            wasteHint={false}
            wasteSuggestions={[]}
            pickedCount={draft.tableIds.length}
            editPax={draft.pax ?? 0}
            onApplySubset={() => {}}
          />
        )}

        {/* TableExplorer reused as the picker — clicking a result
            writes the new tableIds and auto-syncs the zone, same as
            the detail-edit reassign flow. Canvas-plan picker is the
            Fase 2 replacement (task in a follow-up branch). */}
        {explorerOpen && startsAt && endsAt && (
          <TableExplorer
            open
            onOpenChange={setExplorerOpen}
            context="reassign"
            pax={draft.pax ?? 0}
            startsAt={startsAt}
            endsAt={endsAt}
            preferredZoneId={draft.zone || null}
            features={draft.features}
            currentTableIds={draft.tableIds}
            zoneName={(id) => zones.find((z) => z.id === id)?.name ?? "Zona"}
            onPick={(pick) => {
              // ``pick.moves`` is non-empty when the operator picked a
              // "Con redistribución" option (e.g. Mover Fabiana 2→T5,
              // Mover Luisa T4+T5→2). Staging it here so ``submit``
              // ships the chain alongside the create — without this the
              // backend gets just the target mesa, sees it as occupied,
              // and 409s "Mesa(s) ya ocupada(s)".
              updateDraft({
                tableIds: pick.tableIds,
                zone: pick.zoneId ?? draft.zone,
                moves: pick.moves ?? [],
              });
              setExplorerOpen(false);
            }}
          />
        )}

        {/* Notas del cliente — guest-facing notes; the only textarea on
            the surface. Internal notes don't apply at create time
            (there's no operator-only message to record yet). */}
        <div className="flex flex-col gap-1.5">
          <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-text-secondary">
            Notas del cliente
          </div>
          <Textarea
            rows={2}
            value={draft.notes}
            onChange={(e) => updateDraft({ notes: e.target.value })}
            placeholder="Lo que pidió el cliente (sin gluten, mesa cerca de la ventana, etc.)"
            className="resize-none rounded-lg border-dashed bg-white text-[13px] text-text-primary"
          />
        </div>
      </div>

      {/* Footer — Cancelar / Crear reserva. The discard-confirm now
          lives in the header (same slot as the detail panel's cancel
          confirm), so the footer stays consistent across both states.
          Cancelar disables itself while the confirm is open to keep
          the affordance unambiguous. */}
      <div className="flex items-center justify-between gap-2 border-t border-border bg-surface px-5 py-3">
        <Button variant="outline" onClick={attemptClose} disabled={discardConfirm !== null}>
          Cancelar
        </Button>
        <Button
          onClick={submit}
          disabled={
            (!canSubmit && !canSubmitTableless) ||
            !contactIsValid ||
            isSubmitting ||
            discardConfirm !== null
          }
        >
          {isSubmitting ? "Guardando…" : canSubmit ? "Crear reserva" : "Crear sin mesa"}
        </Button>
      </div>
    </div>
  );
}

// ── Pax summary (Día / Noche split, configurable per location) ──

// Fallback when location settings haven't loaded yet (or the field is missing
// on legacy responses). 6 PM matches the model's default.
const DEFAULT_NIGHT_SHIFT_SPLIT_TIME = "18:00:00";

// Inline banner inside the edit form that translates an ``apiDryRun``
// response into the operator's feedback signal: ✅ when the current
// (pax, time, mesa) combination still fits, ⚠️ with a reason code +
// gentle "Cambiar mesa" hint when something is off. Surfaces capacity
// conflicts, kitchen-cap saturation, closed slots, etc. without making
// the operator hit Save first.
function EditDryRunBanner({
  query,
  hasTables,
  hasPendingMoves,
  wasteHint,
  wasteSuggestions,
  pickedCount,
  editPax,
  onApplySubset,
}: {
  query: ReturnType<typeof useQuery<import("@/lib/reservation-api").DryRunResult>>;
  hasTables: boolean;
  /** True when the operator picked a redistribute plan from the
   *  explorer. The pending moves will free the picked combo at save
   *  time, but ``apiDryRun`` doesn't know about them — it sees the
   *  floor as-is, so ``specific_table_busy`` fires as a false
   *  positive. Suppress it here when moves cover the only conflict. */
  hasPendingMoves: boolean;
  /** Whether the picked mesas are oversized for ``editPax``. When
   *  ``true``, ``wasteSuggestions`` carries every smallest-subset
   *  alternative the operator can swap to inline. */
  wasteHint: boolean;
  wasteSuggestions: Array<{ ids: string[]; label: string }>;
  pickedCount: number;
  editPax: number;
  onApplySubset: (ids: string[]) => void;
}) {
  if (!hasTables) {
    return (
      <p className="mt-1.5 text-[11px] text-amber-700">
        Sin mesa asignada — usa "Cambiar" para elegir una.
      </p>
    );
  }
  if (query.isFetching && !query.data) {
    return <p className="mt-1.5 text-[11px] text-text-muted">Validando…</p>;
  }
  if (!query.data) return null;
  if (query.data.would_succeed) {
    if (wasteHint && wasteSuggestions.length > 0) {
      return (
        <div className="mt-1.5 flex flex-col gap-1 text-[11px] text-amber-700">
          <p>
            ⚠ Estás usando {pickedCount} mesas para {editPax}{" "}
            {editPax === 1 ? "persona" : "personas"}. Con menos alcanza:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {wasteSuggestions.map((s) => (
              <button
                key={s.ids.join("|")}
                type="button"
                onClick={() => onApplySubset(s.ids)}
                className="rounded-md border border-amber-500/60 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
              >
                Usar {s.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <p className="mt-1.5 text-[11px] text-green-700">✓ La mesa actual acomoda los cambios</p>
    );
  }
  // Reuse the structured context the backend ships alongside the
  // primary reason. For ``missing_features`` / ``out_of_preferred_zone``
  // we read the same payload the override-confirm dialog renders, so
  // the banner can name exactly what's missing instead of falling back
  // to a generic "no funciona" string.
  const ctx = (query.data.context ?? {}) as Record<string, unknown>;
  const ctxRequiredFeatures = Array.isArray(ctx.required) ? (ctx.required as string[]) : null;
  const ctxPreferredZoneName =
    typeof ctx.preferred_zone_name === "string" && ctx.preferred_zone_name
      ? (ctx.preferred_zone_name as string)
      : null;
  const ctxOutOfZoneNames = Array.isArray(ctx.out_of_zone_table_names)
    ? (ctx.out_of_zone_table_names as string[])
    : null;

  const buildCopy = (code: string): string => {
    switch (code) {
      case "capacity_insufficient":
        return "Esa cantidad de comensales no entra en la mesa actual";
      case "features_unsupported":
      case "missing_features": {
        if (ctxRequiredFeatures && ctxRequiredFeatures.length > 0) {
          const labels = ctxRequiredFeatures
            .map((f) => tableFeatureLabel(f as ApiTableFeature))
            .join(", ");
          return `La mesa actual no soporta: ${labels}`;
        }
        return "La mesa actual no soporta las preferencias pedidas";
      }
      case "no_table_fit":
        return "La mesa no acomoda esos comensales — busca otra";
      case "specific_table_busy":
        return "La mesa está ocupada en ese horario";
      case "out_of_preferred_zone": {
        if (ctxPreferredZoneName && ctxOutOfZoneNames && ctxOutOfZoneNames.length > 0) {
          return `Mesa fuera de ${ctxPreferredZoneName} (${ctxOutOfZoneNames.join(", ")})`;
        }
        return "La mesa actual está fuera de la zona preferida";
      }
      case "zone_inconsistent":
        return "Las mesas elegidas no son consistentes con la zona";
      case "kitchen_cap":
        return "La cocina está saturada en ese horario";
      case "outside_hours_or_closure":
        return "El horario está fuera de servicio";
      case "slot_in_past":
        return "Ese horario ya pasó";
      case "invalid_range":
        return "El rango es inválido";
      default:
        return "No funciona con la mesa actual";
    }
  };

  const allReasons = query.data.reasons?.length
    ? query.data.reasons
    : [query.data.reason ?? "no_table_fit"];
  // The dry-run runs against the floor as-is. When the operator already
  // picked a redistribute plan, drop ``specific_table_busy`` from the
  // list — those mesas WILL be free after the moves apply. If that
  // was the only reason, the banner becomes a positive note.
  const reasons = hasPendingMoves
    ? allReasons.filter((code) => code !== "specific_table_busy")
    : allReasons;
  if (hasPendingMoves && reasons.length === 0) {
    return (
      <p className="mt-1.5 text-[11px] text-green-700">
        ✓ Las mesas estarán libres tras los movimientos
      </p>
    );
  }
  const [primary, ...rest] = reasons;
  return (
    <div className="mt-1.5 flex flex-col gap-1 text-[11px] text-amber-700">
      <p>⚠ {buildCopy(primary)}</p>
      {rest.map((code) => (
        <p key={code} className="ml-3.5 text-[10px] text-amber-600">
          · también: {buildCopy(code)}
        </p>
      ))}
      {/* Even on a failing dry-run, surface the subset chips when the
          picked mesas are oversized: the operator can switch to a
          smaller pick inline instead of opening "Cambiar". Common case
          is a declared TableGroup with a min above the current pax —
          backend says "capacity_insufficient" (too_big), but Mesa X
          sola sí alcanza. */}
      {wasteHint && wasteSuggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {wasteSuggestions.map((s) => (
            <button
              key={s.ids.join("|")}
              type="button"
              onClick={() => onApplySubset(s.ids)}
              className="rounded-md border border-amber-500/60 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
            >
              Usar {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatHHMMtoLabel(value: string): string {
  const [hStr, mStr] = value.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 || 12;
  return `${hh}:${m.toString().padStart(2, "0")} ${ampm}`;
}

/** Parse "HH:MM[:SS]" to fractional hours (e.g. "18:30:00" → 18.5). */
function timeStringToHours(value: string): number {
  const [hStr, mStr] = value.split(":");
  return Number(hStr) + Number(mStr) / 60;
}

function PaxSummary({ className = "" }: { className?: string }) {
  const todayReservations = useTodayReservations();
  const selectedDate = useAppStore((s) => s.selectedDate);
  const { data: openingHours } = useOpeningHours();
  const { data: locationSettings } = useLocationSettings();
  const { isPending: isReservationsPending } = useActiveReservations();

  const splitTime = locationSettings?.night_shift_split_time ?? DEFAULT_NIGHT_SHIFT_SPLIT_TIME;
  const splitHours = timeStringToHours(splitTime);
  const splitLabel = formatHHMMtoLabel(splitTime);

  // dayjs uses 0=Sun..6=Sat; backend uses 0=Mon..6=Sun.
  const dayjsDow = dayjs(selectedDate).day();
  const apiDow = dayjsDow === 0 ? 6 : dayjsDow - 1;
  const todayHours = openingHours?.find((h) => h.day_of_week === apiDow);

  let dayPax = 0;
  let dayCount = 0;
  let nightPax = 0;
  let nightCount = 0;
  for (const r of todayReservations) {
    if (r.status === "noshow") continue;
    const hour = parseTimeHour(r.time);
    if (hour < splitHours) {
      dayPax += r.pax;
      dayCount += 1;
    } else {
      nightPax += r.pax;
      nightCount += 1;
    }
  }

  const openLabel = todayHours ? formatHHMMtoLabel(todayHours.open_time) : null;
  // "00:00:00" close means midnight (end of day) per the API contract.
  const closeLabel = todayHours
    ? todayHours.close_time === "00:00:00"
      ? "12:00 AM"
      : formatHHMMtoLabel(todayHours.close_time)
    : null;

  const dayRange = openLabel ? `${openLabel} – ${splitLabel}` : `Hasta ${splitLabel}`;
  const nightRange = closeLabel ? `${splitLabel} – ${closeLabel}` : `Desde ${splitLabel}`;

  const dayTitle = isReservationsPending
    ? "Cargando…"
    : `Día · ${dayCount} reserva${dayCount === 1 ? "" : "s"} · ${dayRange}`;
  const nightTitle = isReservationsPending
    ? "Cargando…"
    : `Noche · ${nightCount} reserva${nightCount === 1 ? "" : "s"} · ${nightRange}`;

  return (
    <>
      {/* Mobile / Sheet: stacked Day + Night cards. The wrapper inherits the
          grid-positioning className from the parent so the two-card row
          occupies its col-span-2 slot under the date pill. */}
      <div className={`flex lg:hidden w-full gap-2 ${className}`}>
        <div
          className="flex flex-1 items-center gap-2.5 rounded-xl border border-input bg-white px-3 py-1.5"
          title={dayTitle}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100">
            <Sun className="h-4 w-4 text-amber-600" />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold tabular-nums leading-none">
              {isReservationsPending ? "—" : dayPax}
            </span>
            <span className="text-xs text-text-muted">pax</span>
          </div>
        </div>
        <div
          className="flex flex-1 items-center gap-2.5 rounded-xl border border-input bg-white px-3 py-1.5"
          title={nightTitle}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100">
            <Moon className="h-4 w-4 text-indigo-500" />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold tabular-nums leading-none">
              {isReservationsPending ? "—" : nightPax}
            </span>
            <span className="text-xs text-text-muted">pax</span>
          </div>
        </div>
      </div>

      {/* Desktop floating sidebar: compact single-pill keeps the header
          dense when the rail is narrow. */}
      <div
        className={`hidden lg:flex h-8 items-center gap-2 rounded-lg border border-input bg-white px-2.5 text-xs ${className}`}
      >
        <span className="flex items-baseline gap-1" title={dayTitle}>
          <Sun size={12} className="self-center text-amber-500" />
          <span className="font-semibold tabular-nums">{isReservationsPending ? "—" : dayPax}</span>
          <span className="text-[10px] text-text-muted">pax</span>
        </span>
        <span className="h-3 w-px bg-border" />
        <span className="flex items-baseline gap-1" title={nightTitle}>
          <Moon size={12} className="self-center text-indigo-500" />
          <span className="font-semibold tabular-nums">
            {isReservationsPending ? "—" : nightPax}
          </span>
          <span className="text-[10px] text-text-muted">pax</span>
        </span>
      </div>
    </>
  );
}

// ── Main sidebar ────────────────────────────────────────────────
// "espera" is the waitlist tab (SEI-143). Unlike the other three it is not a
// reservation status — it has its own (still-to-come) data source, so no
// ``Reservation["status"]`` ever maps to it. For now its tab renders an empty
// state; the list + actions (llamar / sentar / cancelar / pasar al final)
// land in a follow-up.
const reservationStatusToTab: Record<Reservation["status"], StatusTab> = {
  reservada: "reservadas",
  sentado: "sentadas",
  completada: "finalizadas",
  noshow: "finalizadas",
  cancelada: "finalizadas",
};

export default function Sidebar() {
  const {
    selectedDate,
    setSelectedDate,
    setShowSidebar,
    detailReservationId,
    setDetailReservationId,
    newReservationOpen,
    closeNewReservation,
    newWaitlistOpen,
    closeNewWaitlist,
    detailWaitlistId,
    setDetailWaitlistId,
  } = useAppStore(
    useShallow((s) => ({
      closeNewReservation: s.closeNewReservation,
      closeNewWaitlist: s.closeNewWaitlist,
      detailReservationId: s.detailReservationId,
      detailWaitlistId: s.detailWaitlistId,
      newReservationOpen: s.newReservationOpen,
      newWaitlistOpen: s.newWaitlistOpen,
      selectedDate: s.selectedDate,
      setDetailReservationId: s.setDetailReservationId,
      setDetailWaitlistId: s.setDetailWaitlistId,
      setSelectedDate: s.setSelectedDate,
      setShowSidebar: s.setShowSidebar,
    })),
  );
  const todayReservations = useTodayReservationsAll();
  // `isPending` is true only on the first fetch with no cached data — it
  // flips to false after the initial load and stays there during background
  // refetches, so the spinner doesn't flash on invalidations or window focus.
  const { isPending: isTablesPending } = useTables();
  const { isPending: isReservationsPending } = useActiveReservations();
  const isInitialLoad = isTablesPending || isReservationsPending;
  const [searchQuery, setSearchQuery] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Active tab lives in the store so the desktop "Agregar a lista de espera"
  // button (in DashboardLayout) can show only on the Espera tab.
  const activeTab = useAppStore((s) => s.activeTab);
  const setActiveTab = useAppStore((s) => s.setActiveTab);

  const waitlistItems = useWaitlist().data ?? [];
  const cotizaciones = useCotizaciones().data ?? [];
  const holdLocationTz = useActiveLocationTimezone();
  const holdsByTime = useMemo<Map<string, HoldEntry[]>>(() => {
    // Only merge holds in the "reservadas" tab — holds are upcoming slots.
    if (activeTab !== "reservadas") return new Map();
    const map = new Map<string, HoldEntry[]>();
    for (const q of cotizaciones) {
      if (!q.hold || q.hold.status !== "active") continue;
      const holdDate = dayjs(q.hold.starts_at).tz(holdLocationTz).format("YYYY-MM-DD");
      if (holdDate !== selectedDate) continue;
      const time = formatTime(dayjs(q.hold.starts_at).tz(holdLocationTz));
      const entry: HoldEntry = {
        quoteId: q.id,
        guestName: q.guest_name,
        pax: q.hold.pax,
        time,
        startsAt: q.hold.starts_at,
        tableNames: q.hold.table_names ?? [],
      };
      (map.get(time) ?? (map.set(time, []) && map.get(time)))!.push(entry);
    }
    return map;
  }, [activeTab, cotizaciones, holdLocationTz, selectedDate]);

  // Two display modes drive the list below:
  //  - idle (no search): today's reservations grouped by start time
  //  - search active (>= 2 chars): cross-date hits from the backend, flat &
  //    virtualized — the calendar window is intentionally ignored.
  const trimmedQuery = searchQuery.trim();
  const isSearchActive = trimmedQuery.length >= SEARCH_MIN_LEN;
  // ``useReservationSearch`` MUST run before the detail-view early return so
  // the hook count stays stable across renders where the user clicks a card
  // (which sets ``detailReservationId`` and would otherwise skip this hook,
  // tripping React error #300 — "Rendered fewer hooks than expected").
  const search = useReservationSearch(searchQuery);

  const sourceReservations = isSearchActive ? search.items : todayReservations;

  const statusCounts: Record<StatusTab, number> = {
    reservadas: 0,
    sentadas: 0,
    finalizadas: 0,
    espera: waitlistItems.length,
  };
  const filteredGroups: Record<string, Reservation[]> = {};
  const flatList: Reservation[] = [];
  for (const r of sourceReservations) {
    const bucket = reservationStatusToTab[r.status];
    statusCounts[bucket]++;
    if (bucket !== activeTab) {
      continue;
    }
    if (isSearchActive) {
      flatList.push(r);
    } else {
      (filteredGroups[r.time] ??= []).push(r);
    }
  }
  // Within each time bucket, cancelled rows sink to the bottom and
  // sort by ``cancelledAt`` descending — the most recent cancellation
  // first, so when the operator opens Finalizadas to follow up on the
  // last cancel they see it on top of the slot. Active rows above
  // keep their incoming order.
  for (const time of Object.keys(filteredGroups)) {
    filteredGroups[time] = filteredGroups[time].toSorted((a, b) => {
      const aCancelled = a.status === "cancelada";
      const bCancelled = b.status === "cancelada";
      if (aCancelled !== bCancelled) return aCancelled ? 1 : -1;
      if (aCancelled && bCancelled) {
        const aT = a.cancelledAt ? Date.parse(a.cancelledAt) : 0;
        const bT = b.cancelledAt ? Date.parse(b.cancelledAt) : 0;
        return bT - aT;
      }
      return 0;
    });
  }
  // Reservadas reads next-up-first; Sentadas/Finalizadas read most-recent-first,
  // so the slot that just gained a card sits on top.
  const sortedGroupEntries = Object.entries(filteredGroups).toSorted(([a], [b]) => {
    const delta = parseTime(a).valueOf() - parseTime(b).valueOf();
    return activeTab === "reservadas" ? delta : -delta;
  });
  // Newest first when searching — staff most often look up an upcoming or
  // recent booking, not something from a year ago.
  const sortedFlatList = isSearchActive
    ? [...flatList].toSorted((a, b) => b.createdAt - a.createdAt)
    : flatList;
  const hasResults = isSearchActive
    ? sortedFlatList.length > 0
    : sortedGroupEntries.length > 0 || holdsByTime.size > 0;

  // Build the row stream the virtualizer renders: a date header is inserted
  // whenever the date flips, so cross-date results stay readable inside the
  // flat list. When the active tab is empty for this batch, the loop produces
  // no items and the empty state takes over below.
  const searchRows: SearchRow[] = [];
  if (isSearchActive) {
    let prevDate = "";
    for (const r of sortedFlatList) {
      if (r.date !== prevDate) {
        searchRows.push({
          kind: "header",
          key: `h-${r.date}`,
          label: dayjs(r.date)
            .format("dddd, D [de] MMMM YYYY")
            .replace(/^\w/, (c) => c.toUpperCase()),
        });
        prevDate = r.date;
      }
      searchRows.push({ kind: "item", key: r.id, reservation: r });
    }
  }

  const handleReservationSelect = (res: Reservation) => {
    // Cross-date search hits live outside the day-sliced live store, so the
    // detail panel (which reads from ``useTodayReservationsAll``) needs the
    // sidebar's selectedDate to match the reservation's date or it renders
    // an empty panel.
    if (res.date !== selectedDate) {
      setSelectedDate(res.date);
    }
    setDetailReservationId(res.id);
    // Map focus only fires for items already in the live store (today). A
    // search hit on a different date won't be there, and that's fine — we
    // just open the detail without panning the map.
    const live = useAppStore.getState().reservations.find((row) => row.id === res.id);
    if (!live) return;
    const shouldFocus =
      live.status === "sentado" || (live.status === "reservada" && isReservationSoon(live));
    if (shouldFocus) {
      if (live.tableIds.length > 0) {
        setFocusTableIds(live.tableIds);
      } else {
        requestMapFit();
      }
    }
  };

  return (
    <TooltipProvider>
      <aside className="@container relative w-full shrink-0 bg-surface-cream lg:bg-surface flex flex-col h-full overflow-clip lg:border lg:border-[#d9d9d9] lg:rounded-2xl lg:shadow-lg">
        {/* Header */}
        <div className="flex flex-col gap-4 p-5 pb-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSidebar(false)}
            className="lg:hidden self-end"
          >
            <svg
              className="w-5 h-5 text-text-secondary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Button>

          {/* Date picker */}
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 @md:flex">
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger className="inline-flex w-fit h-9 lg:h-8 items-center gap-2 rounded-[10px] lg:rounded-lg border border-input bg-white px-3 lg:px-2.5 text-sm font-semibold text-text-primary transition-colors hover:bg-accent @md:order-1">
                <CalendarIcon className="h-4 w-4 text-text-secondary" />
                <span className="capitalize">
                  {dayjs(selectedDate).format("ddd D MMM")}
                  {dayjs(selectedDate).year() !== dayjs().year() &&
                    ` ${dayjs(selectedDate).format("YYYY")}`}
                </span>
                {selectedDate === todayStr() && (
                  <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-text-secondary">
                    Hoy
                  </span>
                )}
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dayjs(selectedDate).toDate()}
                  onSelect={(date) => {
                    if (date) {
                      setSelectedDate(dayjs(date).format("YYYY-MM-DD"));
                      setCalendarOpen(false);
                    }
                  }}
                />
              </PopoverContent>
            </Popover>
            <div className="flex items-center gap-1 @md:order-3">
              {selectedDate !== todayStr() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDate(todayStr())}
                  aria-label="Volver a hoy"
                  className="h-9 lg:h-7 gap-1.5 px-3 lg:px-2 rounded-[10px] lg:rounded-md text-sm lg:text-xs font-semibold"
                >
                  <CalendarCheck className="h-4 w-4 lg:h-3.5 lg:w-3.5" />
                  Hoy
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() =>
                  setSelectedDate(dayjs(selectedDate).subtract(1, "day").format("YYYY-MM-DD"))
                }
                aria-label="Día anterior"
                className="h-9 w-9 lg:h-7 lg:w-7 p-0 rounded-[10px] lg:rounded-md border border-input lg:border-0 bg-white lg:bg-transparent text-text-primary lg:text-text-secondary"
              >
                <ChevronLeft className="h-[18px] w-[18px] lg:h-3.5 lg:w-3.5" />
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  setSelectedDate(dayjs(selectedDate).add(1, "day").format("YYYY-MM-DD"))
                }
                aria-label="Día siguiente"
                className="h-9 w-9 lg:h-7 lg:w-7 p-0 rounded-[10px] lg:rounded-md border border-input lg:border-0 bg-white lg:bg-transparent text-text-primary lg:text-text-secondary"
              >
                <ChevronRight className="h-[18px] w-[18px] lg:h-3.5 lg:w-3.5" />
              </Button>
            </div>
            <PaxSummary className="col-span-2 @md:order-2 @md:mr-auto lg:justify-self-start" />
          </div>

          {/* Search — default sizing matches the mobile/Sheet design;
              lg: collapses back to the compact desktop pill that fits the
              460-px floating rail. Viewport-based (not pointer-coarse) so
              DevTools mobile emulation reflects the real mobile size. */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 lg:left-2.5 top-1/2 h-4 w-4 lg:h-3.5 lg:w-3.5 -translate-y-1/2 text-text-muted" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar reserva o cliente…"
              className="h-11 lg:h-8 pl-10 lg:pl-8 pr-3.5 lg:pr-2 text-sm lg:text-xs bg-white rounded-xl lg:rounded-lg"
            />
          </div>

          {/* Status tabs — flex-1 buttons grow to fill when there's room; the
              Button base is shrink-0 + whitespace-nowrap, so once a 4th tab
              stops them fitting they keep their natural width and the row
              scrolls horizontally instead of squashing. scrollbar-none hides
              the bar (the scroll stays, the chrome doesn't). */}
          <div className="flex gap-1 overflow-x-auto p-1 lg:p-0.5 bg-surface-alt rounded-xl lg:rounded-lg scrollbar-none">
            {(
              [
                { id: "reservadas", label: "Reservadas" },
                { id: "sentadas", label: "Sentadas" },
                { id: "finalizadas", label: "Finalizadas" },
                { id: "espera", label: "Espera" },
              ] as { id: StatusTab; label: string }[]
            ).map((t) => {
              const count = statusCounts[t.id];
              const isActive = activeTab === t.id;
              return (
                <Button
                  key={t.id}
                  variant="ghost"
                  onClick={() => setActiveTab(t.id)}
                  className={`flex-1 gap-1.5 h-10 lg:h-7 py-0 px-2 rounded-lg lg:rounded-md text-[13px] lg:text-[11px] font-semibold cursor-pointer ${isActive ? "bg-white text-text-primary shadow-sm hover:bg-white hover:text-text-primary" : "text-text-secondary"}`}
                >
                  {t.label}
                  <span
                    className={`text-[11px] lg:text-[10px] px-2 lg:px-1.5 py-0.5 lg:py-px rounded-full ${isActive ? "bg-dark text-white" : "bg-border text-text-secondary"}`}
                  >
                    {count}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* List */}
        <SidebarReservationsList
          isInitialLoad={isInitialLoad}
          isSearchActive={isSearchActive}
          searchQuery={searchQuery}
          search={search}
          activeTab={activeTab}
          hasResults={hasResults}
          sortedGroupEntries={sortedGroupEntries}
          holdsByTime={holdsByTime}
          searchRows={searchRows}
          onSelect={handleReservationSelect}
          waitlistItems={waitlistItems}
        />
        {/* Detail panel overlays the list instead of replacing it, so the
            list keeps its scroll position while a reservation is open. */}
        {detailReservationId && (
          <div className="absolute inset-0 z-20 flex flex-col bg-surface">
            <DetailView
              detailId={detailReservationId}
              onClose={() => {
                setDetailReservationId(null);
                requestMapFit();
              }}
            />
          </div>
        )}
        {/* Nueva reserva surface — overlays the list the same way the
            detail panel does. Coexists with DetailView for now; the
            entry-point re-route in a follow-up commit will make this
            the primary path (modal stays for fallback). */}
        {newReservationOpen && (
          <div className="absolute inset-0 z-20 flex flex-col bg-surface">
            <NewReservationView
              onClose={() => {
                closeNewReservation();
                requestMapFit();
              }}
            />
          </div>
        )}
        {detailWaitlistId && (
          <div className="absolute inset-0 z-20 flex flex-col bg-surface">
            <WaitlistDetailView
              entryId={detailWaitlistId}
              onClose={() => setDetailWaitlistId(null)}
            />
          </div>
        )}
        {/* Nueva entrada de lista de espera — same overlay pattern as
            the new-reservation surface for paradigm consistency. */}
        {newWaitlistOpen && (
          <div className="absolute inset-0 z-20 flex flex-col bg-surface">
            <NewWaitlistView onClose={closeNewWaitlist} />
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}

// ── Reservations list (virtualized when searching, grouped-by-time when idle) ──

type SearchRow =
  | { kind: "header"; key: string; label: string }
  | { kind: "item"; key: string; reservation: Reservation };

interface HoldEntry {
  quoteId: string;
  guestName: string;
  pax: number;
  time: string;
  startsAt: string;
  tableNames: string[];
}

function HoldCard({ entry, onClick }: { entry: HoldEntry; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-[10px] border border-[#e0dbd0] bg-[#fafaf7] p-3 text-left transition hover:bg-[#f5f4ef]"
    >
      <div className="mb-1.5 inline-flex w-fit items-center rounded-full border border-[#b13030] bg-white px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-[#b13030]">
        HOLD
      </div>
      <div className="text-[19px] font-bold leading-tight tracking-tight text-text-primary">
        {entry.guestName}
      </div>
      <div className="mt-1 text-xs text-text-secondary">
        {entry.pax} pax · {entry.time}
      </div>
      {entry.tableNames.length > 0 && (
        <div className="mt-0.5 text-xs text-muted-foreground">{entry.tableNames.join(", ")}</div>
      )}
    </button>
  );
}

function SidebarReservationsList({
  isInitialLoad,
  isSearchActive,
  searchQuery,
  search,
  activeTab,
  hasResults,
  sortedGroupEntries,
  holdsByTime,
  searchRows,
  onSelect,
  waitlistItems,
}: {
  isInitialLoad: boolean;
  isSearchActive: boolean;
  searchQuery: string;
  search: ReturnType<typeof useReservationSearch>;
  activeTab: StatusTab;
  hasResults: boolean;
  sortedGroupEntries: [string, Reservation[]][];
  holdsByTime: Map<string, HoldEntry[]>;
  searchRows: SearchRow[];
  onSelect: (reservation: Reservation) => void;
  waitlistItems: ApiWaitlistEntry[];
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Subscribed read so the group highlight redraws when previewTime toggles.
  const previewTime = useAppStore((s) => s.previewTime);
  const navigate = useNavigate();

  // Scroll restore: on desktop the list keeps its scroll naturally
  // when the detail overlay mounts (it isn't unmounted, just
  // covered). On mobile something resets the scrollTop on close —
  // probably the safe-area/viewport reflow that fires when the
  // overlay un-mounts. Snapshot the position when detail opens and
  // restore it after close so the operator lands back on the row
  // they clicked.
  const detailOpenId = useAppStore((s) => s.detailReservationId);
  const savedScrollTop = useRef(0);
  const prevDetailId = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevDetailId.current;
    if (prev === null && detailOpenId !== null) {
      if (scrollRef.current) savedScrollTop.current = scrollRef.current.scrollTop;
    } else if (prev !== null && detailOpenId === null) {
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = savedScrollTop.current;
      });
    }
    prevDetailId.current = detailOpenId;
  }, [detailOpenId]);

  // Precompute the "walk-in on my mesa" badge for every card the list
  // will render. Done once at the parent so the per-card cost stays
  // O(N×M) total rather than each ``ReservationCard`` subscribing to
  // ``useLiveReservations`` and re-running the filter on its own —
  // which was O(N²) per minute tick. The minute tick below keeps the
  // map fresh as SEATED parties' end times tick past now.
  useMinuteTick();
  const liveReservations = useLiveReservations();
  const listLocationTz = useActiveLocationTimezone();
  const walkinByReservation = useMemo(
    () => computeWalkinOnMyMesaMap(liveReservations, liveReservations, dayjs(), listLocationTz),
    // ``dayjs()`` reads the current minute; ``useMinuteTick`` re-renders
    // the parent every 60 s so this useMemo recomputes on schedule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveReservations, listLocationTz],
  );

  // Virtualizer always runs; ``count`` is 0 when not searching, which is a
  // no-op render. Keeping the hook unconditional avoids tripping React's
  // rules-of-hooks when the user toggles search on and off.
  const rowVirtualizer = useVirtualizer({
    count: searchRows.length,
    getScrollElement: () => scrollRef.current,
    // Cards run ~96px, headers ~44px. We use ``measureElement`` below to
    // get exact heights once mounted, so this is just the initial guess.
    estimateSize: (index) => (searchRows[index]?.kind === "header" ? 44 : 96),
    overscan: 6,
    getItemKey: (index) => searchRows[index]?.key ?? index,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const lastVisibleIndex =
    virtualItems.length > 0 ? virtualItems[virtualItems.length - 1].index : 0;

  // Pull the next page once the user is within ~5 rows of the end. Re-checks
  // on every scroll and on every layout change of the virtual window.
  useEffect(() => {
    if (!isSearchActive) return;
    if (!search.hasNextPage || search.isFetchingNextPage) return;
    if (lastVisibleIndex >= searchRows.length - 5) {
      search.fetchNextPage();
    }
  }, [isSearchActive, lastVisibleIndex, searchRows.length, search]);

  const showSearchSpinner = isSearchActive && (search.isPending || search.isDebouncing);
  const showSearchEmpty =
    isSearchActive && !search.isPending && !search.isDebouncing && !hasResults;

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-3">
      {isInitialLoad && !isSearchActive && (
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-6 text-text-secondary" />
        </div>
      )}

      {showSearchSpinner && (
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-6 text-text-secondary" />
        </div>
      )}

      {/* Espera — quick affordance to mount the host-stand QR display on a
          tablet. Visible regardless of queue state so the host can prepare
          the screen before the first guest arrives. */}
      {!isInitialLoad && activeTab === "espera" && (
        <div className="mb-3 flex justify-end">
          <a
            href="/waitlist-qr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#c8c8c2] bg-white px-2.5 py-1 text-xs font-medium text-text-primary hover:bg-[#f7f6f3]"
          >
            <QrCode className="size-3.5" />
            Mostrar QR del local
          </a>
        </div>
      )}

      {!isInitialLoad &&
        !isSearchActive &&
        !hasResults &&
        !(activeTab === "espera" && waitlistItems.length > 0) && (
          <div className="text-center text-sm text-text-muted py-8">
            {activeTab === "reservadas"
              ? "No hay reservas pendientes"
              : activeTab === "sentadas"
                ? "No hay reservas sentadas"
                : activeTab === "finalizadas"
                  ? "No hay reservas finalizadas"
                  : "Todavía no hay nadie en espera"}
          </div>
        )}

      {showSearchEmpty && (
        <div className="text-center py-8">
          <p className="text-sm text-text-muted">No se encontró ninguna reserva</p>
          <p className="text-xs text-text-muted mt-1">para "{searchQuery}"</p>
        </div>
      )}

      {!isInitialLoad && activeTab === "espera" && waitlistItems.length > 0 && (
        <div className="flex flex-col gap-2">
          {waitlistItems.map((entry) => (
            <WaitlistCard key={entry.id} entry={entry} />
          ))}
        </div>
      )}

      {!isInitialLoad && !isSearchActive && hasResults && activeTab !== "espera" && (
        <div className="flex flex-col gap-4">
          {(() => {
            // Merge reservation times and hold-only times into one sorted list.
            // When holdsByTime is empty this reduces to exactly the original path.
            const resoTimeSet = new Set(sortedGroupEntries.map(([t]) => t));
            const allTimes =
              holdsByTime.size === 0
                ? sortedGroupEntries.map(([t]) => t)
                : [
                    ...resoTimeSet,
                    ...[...holdsByTime.keys()].filter((t) => !resoTimeSet.has(t)),
                  ].toSorted((a, b) => parseTime(a).valueOf() - parseTime(b).valueOf());
            return allTimes.map((time) => {
              const resos = sortedGroupEntries.find(([t]) => t === time)?.[1] ?? [];
              const holds = holdsByTime.get(time) ?? [];
              const paxTotal = resos.reduce((sum, r) => sum + r.pax, 0);
              const isPreviewing = previewTime === time;
              const hasResos = resos.length > 0;
              return (
                <div
                  className={`flex flex-col gap-2 rounded-xl transition ${
                    isPreviewing ? "bg-amber-50/70 ring-1 ring-amber-200 px-2 py-2 -mx-2" : ""
                  }`}
                  key={time}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewTime(isPreviewing ? null : time);
                    }}
                    title={isPreviewing ? "Volver a tiempo real" : `Simular mapa a las ${time}`}
                    aria-pressed={isPreviewing}
                    className={`flex items-center justify-between rounded-md px-1 py-0.5 text-left transition hover:bg-orange-50 ${
                      isPreviewing ? "text-amber-900" : ""
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <Clock
                        className={`size-3.5 ${isPreviewing ? "text-amber-700" : "text-text-muted"}`}
                      />
                      <span className="text-xs font-semibold">{time}</span>
                      {isPreviewing && (
                        <span className="rounded-full bg-amber-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                          simulando
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-text-muted">
                      {hasResos
                        ? `${resos.length} reserva${resos.length !== 1 ? "s" : ""} · ${paxTotal} pax${holds.length > 0 ? ` · ${holds.length} en hold` : ""}`
                        : `${holds.length} en hold`}
                    </span>
                  </button>
                  {resos.map((r) => (
                    <ReservationCard
                      key={r.id}
                      r={r}
                      onSelect={onSelect}
                      walkinOnMyMesa={walkinByReservation.get(r.id) ?? null}
                    />
                  ))}
                  {holds.map((h) => (
                    <HoldCard
                      key={h.quoteId}
                      entry={h}
                      onClick={() => navigate({ to: "/cotizaciones", search: { open: h.quoteId } })}
                    />
                  ))}
                </div>
              );
            });
          })()}
        </div>
      )}

      {isSearchActive && hasResults && (
        <>
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: "relative",
              width: "100%",
            }}
          >
            {virtualItems.map((vi) => {
              const row = searchRows[vi.index];
              if (!row) return null;
              return (
                <div
                  key={vi.key}
                  data-index={vi.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${vi.start}px)`,
                  }}
                >
                  {row.kind === "header" ? (
                    <div className="pt-3 pb-2 first:pt-0">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                        {row.label}
                      </span>
                    </div>
                  ) : (
                    <div className="pb-2">
                      <ReservationCard
                        r={row.reservation}
                        onSelect={onSelect}
                        walkinOnMyMesa={walkinByReservation.get(row.reservation.id) ?? null}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Loader for the next page sits OUTSIDE the position:relative virtual
              container so it lays out below the absolutely-positioned rows; if
              it lived inside, it would render at top:0 and get visually pinned
              under the first row. */}
          {search.isFetchingNextPage && (
            <div className="flex items-center justify-center py-4">
              <Spinner className="size-5 text-text-secondary" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
